import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { custodianAPI } from '../../services/api';
import { sendAuditLog } from '../../utils/auditLog';
import { useAuth } from '../../contexts/AuthContext';
import { CUSTODIAN_PERMISSIONS, usePermissions } from '../../utils/permissionUtils';
import { toast } from 'sonner';
import {
    UserCheck,
    FileText,
    Shield,
    CheckCircle,
    XCircle,
    Clock,
    AlertTriangle,
    AlertCircle,
    Search,
    Eye,
    Download,
    Copy,
    User,
    Building2,
    MessageSquare,
    Plus,
    Key,
    MoreHorizontal,
    Trash2,
    CheckSquare,
    XSquare,
    X,
    Wallet,
    ArrowLeft
} from 'lucide-react';

const SubInvestor = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('sub-investors');
    const [activeView, setActiveView] = useState('list');
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [reviewNotes, setReviewNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [requestMoreInfo, setRequestMoreInfo] = useState('');
    const [showDecisionModal, setShowDecisionModal] = useState(null);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [selectedSubInvestor, setSelectedSubInvestor] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [resetPasswordResult, setResetPasswordResult] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showActionsDropdown, setShowActionsDropdown] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const actionButtonRefs = useRef({});
    const [showApproveL2Modal, setShowApproveL2Modal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [approvalComments, setApprovalComments] = useState('');
    const [approvalRejectionReason, setApprovalRejectionReason] = useState('');
    const [approvalRejectionComments, setApprovalRejectionComments] = useState('');

    // State for pending approval actions
    const [selectedPendingRequest, setSelectedPendingRequest] = useState(null);
    const [showApproveRequestModal, setShowApproveRequestModal] = useState(false);
    const [showRejectRequestModal, setShowRejectRequestModal] = useState(false);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [subInvestorWallets, setSubInvestorWallets] = useState([]);
    const [editingWallet, setEditingWallet] = useState(null);
    const [editBankAccountForm, setEditBankAccountForm] = useState({});

    // New investor form state
    const [newInvestorForm, setNewInvestorForm] = useState({
        applicationType: 'KYB',
        name: '',
        email: '',
        initialPassword: '',
        registrationNumber: '',
        phone: '',
        address: '',
        entityType: '',
        incorporationDate: '',
        ssaName: '',
        ssaAccountNumber: '',
        custodyAccountNumber: '',
        residencyStatus: '',
        lei: ''
    });

    // Bank account inheritance state
    const [availableAccountTypes, setAvailableAccountTypes] = useState([]);
    const [selectedAccountTypes, setSelectedAccountTypes] = useState([]);
    const [bankAccountForms, setBankAccountForms] = useState({});
    const [batchFile, setBatchFile] = useState([]);
    const [batchUploadResults, setBatchUploadResults] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    // Fetch custodian wallet information on component mount
    useEffect(() => {
        const fetchCustodianWallets = async () => {
            try {
                const response = await custodianAPI.getCustodianWallets();
                if (response.data.success) {
                    // setCustodianWallets(response.data.wallets); // wallets not used for display
                    setAvailableAccountTypes(response.data.availableAccountTypes);

                    // Auto-select account types based on custodian's available types
                    if (response.data.availableAccountTypes.length === 1) {
                        setSelectedAccountTypes(response.data.availableAccountTypes);
                        // Initialize form for single account type
                        const accountType = response.data.availableAccountTypes[0];
                        setBankAccountForms({
                            [accountType]: {
                                bankName: '',
                                accountName: '',
                                accountNumber: '',
                                accountCategory: 'current',
                                swiftBic: '',
                                branchCode: '',
                                shariahComplianceDocument: null
                            }
                        });
                    }
                }
            } catch (error) {
                toast.error('Failed to load custodian wallet information');
            }
        };

        fetchCustodianWallets();
    }, []);

    // Edit sub-investor form state
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        phone: '',
        registrationNumber: '',
        entityType: '',
        address: '',
        incorporationDate: '',
        ssaName: '',
        ssaAccountNumber: '',
        custodyAccountNumber: '',
        residencyStatus: '',
        lei: ''
    });

    // Create sub-investor mutation
    const createInvestorMutation = useMutation({
        mutationFn: (subInvestorData) => custodianAPI.createSubInvestor(subInvestorData),
        onSuccess: async (response, variables) => {
            // Audit log: sub-investor creation
            if (response?.data?._id || response?.data?.id) {
                await sendAuditLog({
                    action: 'create_sub_investor',
                    details: `Created sub-investor: ${response?.data?.name || response?.data?.email || response?.data?._id || response?.data?.id}`,
                    userId: user?.id,
                });
            }

            // Reset form
            setNewInvestorForm({
                applicationType: 'KYB',
                name: '',
                email: '',
                initialPassword: '',
                registrationNumber: '',
                phone: '',
                address: '',
                entityType: '',
                incorporationDate: '',
            });
            // setUploadedDocuments([]); // documents not tracked in state
            setSelectedAccountTypes([]);
            setBankAccountForms({});

            // Invalidate and refetch the users query to get fresh data
            await queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);

            // After refetch, find and select the newly created sub-investor
            const newSubInvestorEmail = response?.data?.subInvestor?.email || response?.data?.email || variables.email;
            const updatedData = queryClient.getQueryData(['custodian-sub-investors', user?.id]);
            const newSubInvestor = updatedData?.find(app => app.email === newSubInvestorEmail);

            if (newSubInvestor) {
                // Select the newly created sub-investor (password now comes from DB)
                setSelectedApplication(newSubInvestor);
                setActiveView('details');
            } else {
                setActiveView('list');
            }

            // Show success message
            toast.success('Investor created successfully! View details to see credentials.');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to create investor. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Batch upload mutation
    const batchUploadMutation = useMutation({
        mutationFn: (csvData) => custodianAPI.batchUpload(csvData),
        onSuccess: (response) => {
            setBatchUploadResults(response.data.results);
            setIsUploading(false);

            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);

            // Show success message
            const { successful, failed, total } = response.data.results;
            toast.success(`Batch upload completed! Successful: ${successful}, Failed: ${failed}, Total: ${total}`);
        },
        onError: (error) => {
            setIsUploading(false);

            const errorMessage = error.response?.data?.message || 'Failed to process batch upload. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Activate sub-investor mutation
    const activateSubInvestorMutation = useMutation({
        mutationFn: (subInvestorId) => custodianAPI.activateSubInvestor(subInvestorId),
        onSuccess: async (response) => {
            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);

            // Audit log for L2 approve activate
            if (showActivateModal === 'activate' && selectedSubInvestor) {
                await sendAuditLog({
                    action: 'l2_approve_activate',
                    details: `L2 approved activation for sub-investor: ${selectedSubInvestor.applicantName || selectedSubInvestor.email || selectedSubInvestor.userId}`,
                    userId: user?.id,
                });
            }

            // Show success message
            toast.success('Sub-investor activated successfully!');
            setShowActivateModal(false);
            setSelectedSubInvestor(null);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to activate sub-investor. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Deactivate sub-investor mutation
    const deactivateSubInvestorMutation = useMutation({
        mutationFn: (subInvestorId) => custodianAPI.deactivateSubInvestor(subInvestorId),
        onSuccess: async (response) => {
            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);

            // Audit log for L2 approve deactivate
            if (showActivateModal === 'deactivate' && selectedSubInvestor) {
                await sendAuditLog({
                    action: 'l2_approve_deactivate',
                    details: `L2 approved deactivation for sub-investor: ${selectedSubInvestor.applicantName || selectedSubInvestor.email || selectedSubInvestor.userId}`,
                    userId: user?.id,
                });
            }

            // Show success message
            toast.success('Sub-investor deactivated successfully!');
            setShowActivateModal(false);
            setSelectedSubInvestor(null);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to deactivate sub-investor. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Update sub-investor mutation
    const updateSubInvestorMutation = useMutation({
        mutationFn: ({ subInvestorId, updateData }) => custodianAPI.updateSubInvestor(subInvestorId, updateData),
        onSuccess: (response) => {
            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);

            // Show success message
            toast.success('Sub-investor updated successfully!');
            setShowEditModal(false);
            setSelectedSubInvestor(null);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to update sub-investor. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Reset sub-investor password mutation
    const resetPasswordMutation = useMutation({
        mutationFn: (subInvestorId) => custodianAPI.resetSubInvestorPassword(subInvestorId),
        onSuccess: (response) => {
            // Show success message and new password
            setResetPasswordResult(response.data.newPassword);
            toast.success('Sub-investor password reset successfully!');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to reset sub-investor password. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Delete sub-investor mutation
    const deleteSubInvestorMutation = useMutation({
        mutationFn: (subInvestorId) => custodianAPI.deleteSubInvestor(subInvestorId),
        onSuccess: (response) => {
            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);

            // Show success message
            toast.success('Sub-investor deleted successfully!');
            setShowDeleteModal(false);
            setSelectedSubInvestor(null);
            setShowActionsDropdown(null);
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to delete sub-investor. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Approve L2 mutation
    const approveL2Mutation = useMutation({
        mutationFn: ({ subInvestorId, comments }) => custodianAPI.approveL2(subInvestorId, comments),
        onSuccess: async (response) => {
            // Audit log: L2 approval for sub-investor
            if (response?.data?._id || response?.data?.id) {
                await sendAuditLog({
                    action: 'approve_l2_sub_investor',
                    details: `L2 approved sub-investor: ${response?.data?.name || response?.data?.email || response?.data?._id || response?.data?.id}`,
                    userId: user?.id,
                });
            }
            // Close modal and reset form
            setShowApproveL2Modal(false);
            setSelectedSubInvestor(null);
            setApprovalComments('');
            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);
            queryClient.invalidateQueries(['custodian-approvable-sub-investors', user?.id]);
            // Show success message
            toast.success('L2 approval granted successfully! Sub-investor account is now active.');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to approve L2. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Reject sub-investor mutation
    const rejectSubInvestorMutation = useMutation({
        mutationFn: ({ subInvestorId, rejectionReason, comments }) =>
            custodianAPI.rejectSubInvestor(subInvestorId, rejectionReason, comments),
        onSuccess: (response) => {
            // Close modal and reset form
            setShowRejectModal(false);
            setSelectedSubInvestor(null);
            setApprovalRejectionReason('');
            setApprovalRejectionComments('');

            // Invalidate and refetch the users query to get fresh data
            queryClient.invalidateQueries(['custodian-sub-investors', user?.id]);
            queryClient.invalidateQueries(['custodian-approvable-sub-investors', user?.id]);

            // Show success message
            toast.success('Sub-investor rejected successfully!');
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.message || 'Failed to reject sub-investor. Please try again.';
            toast.error(`Error: ${errorMessage}`);
        }
    });

    // Query to fetch sub-investors created by current custodian
    const {
        data: subInvestorsData,
        isLoading: subInvestorsLoading,
        error: subInvestorsError,
        refetch: refetchSubInvestors
    } = useQuery({
        queryKey: ['custodian-sub-investors', user?.id],
        queryFn: async () => {
            const response = await custodianAPI.getSubInvestors({
                limit: 100
            });

            // Fetch wallets for each sub-investor
            const subInvestorsWithWallets = await Promise.all(
                response.data.subInvestors.map(async (apiUser) => {
                    try {
                        const walletsResponse = await custodianAPI.getSubInvestorWallets(apiUser._id);
                        const wallets = walletsResponse?.data?.wallets || [];

                        // Separate wallets by type
                        const conventionalWallet = wallets.find(w => w.bankAccountDetails?.accountType === 'conventional');
                        const islamicWallet = wallets.find(w => w.bankAccountDetails?.accountType === 'islamic');

                        return {
                            ...apiUser,
                            conventionalWalletAddress: conventionalWallet?.walletAddress || '-',
                            islamicWalletAddress: islamicWallet?.walletAddress || '-',
                            conventionalBankAccount: conventionalWallet?.bankAccountDetails ? {
                                bankName: conventionalWallet.bankAccountDetails.bankName || '-',
                                accountNumber: conventionalWallet.bankAccountDetails.accountNumber || '-'
                            } : { bankName: '-', accountNumber: '-' },
                            islamicBankAccount: islamicWallet?.bankAccountDetails ? {
                                bankName: islamicWallet.bankAccountDetails.bankName || '-',
                                accountNumber: islamicWallet.bankAccountDetails.accountNumber || '-'
                            } : { bankName: '-', accountNumber: '-' }
                        };
                    } catch (error) {
                        console.error(`Error fetching wallets for sub-investor ${apiUser._id}:`, error);
                        return {
                            ...apiUser,
                            conventionalWalletAddress: '-',
                            islamicWalletAddress: '-',
                            conventionalBankAccount: { bankName: '-', accountNumber: '-' },
                            islamicBankAccount: { bankName: '-', accountNumber: '-' }
                        };
                    }
                })
            );

            return {
                ...response,
                data: {
                    ...response.data,
                    subInvestors: subInvestorsWithWallets
                }
            };
        },
        enabled: !!user?.id && user?.role === 'custodian',
        select: (data) => {
            // Transform the API response to match the expected format
            return data.data.subInvestors.map(apiUser => ({
                id: `KYB-${apiUser._id}`,
                applicantName: apiUser.corporateInfo?.companyName || apiUser.corporateInfo?.legalEntityName || 'N/A',
                applicationType: 'KYB',
                submissionDate: apiUser.createdAt || new Date().toISOString(),
                status: apiUser.approvalStatus === 'approved' ? 'approved' :
                    apiUser.approvalStatus === 'pending_l2' ? 'pending_l2' :
                        apiUser.approvalStatus === 'rejected' ? 'rejected' : 'pending_l1',
                accountStatus: apiUser.isActive ? 'active' : 'closed', // Add account status
                approvalStatus: apiUser.approvalStatus, // Add approval status
                createdBy: apiUser.createdBy, // Add createdBy field
                email: apiUser.email,
                phone: apiUser.corporateInfo?.phoneNumber || '',
                registrationNumber: apiUser.corporateInfo?.businessRegistrationNumber || '',
                entityType: apiUser.corporateInfo?.registrationType || '',
                address: apiUser.corporateInfo?.registeredAddress || '',
                incorporationDate: apiUser.corporateInfo?.dateOfIncorporation || '',
                ssaName: apiUser.corporateInfo?.ssaName || '',
                ssaAccountNumber: apiUser.corporateInfo?.ssaAccountNumber || '',
                custodyAccountNumber: apiUser.corporateInfo?.custodyAccountNumber || '',
                residencyStatus: apiUser.corporateInfo?.residencyStatus || '',
                lei: apiUser.lei || '',
                documents: [],
                amlScreening: {
                    pepStatus: 'Not PEP',
                    sanctionsCheck: 'Clear',
                    adverseMedia: 'None Found',
                    riskRating: 'Low'
                },
                userId: apiUser._id, // Store the actual user ID for reference
                // Add wallet data
                conventionalWalletAddress: apiUser.conventionalWalletAddress || '-',
                islamicWalletAddress: apiUser.islamicWalletAddress || '-',
                conventionalBankAccount: apiUser.conventionalBankAccount || { bankName: '-', accountNumber: '-' },
                islamicBankAccount: apiUser.islamicBankAccount || { bankName: '-', accountNumber: '-' },
                // Add temporary password if available
                temporaryPassword: apiUser.temporaryPassword || null
            }));
        }
    });

    // Query to fetch sub-investors that current custodian can approve
    // Commented out - currently unused but may be needed for future approval workflow
    // const {
    //     data: approvableSubInvestorsData,
    //     isLoading: approvableSubInvestorsLoading,
    //     error: approvableSubInvestorsError,
    //     refetch: refetchApprovableSubInvestors
    // } = useQuery({
    //     queryKey: ['custodian-approvable-sub-investors', user?.id],
    //     queryFn: () => custodianAPI.getApprovableSubInvestors({
    //         limit: 100
    //     }),
    //     enabled: !!user?.id && user?.role === 'custodian',
    //     select: (data) => {
    //         // Transform the API response to match the expected format
    //         return data.data.approvableSubInvestors.map(apiUser => ({
    //             id: `KYB-${apiUser._id}`,
    //             applicantName: apiUser.corporateInfo?.companyName || apiUser.corporateInfo?.legalEntityName || 'N/A',
    //             applicationType: 'KYB',
    //             submissionDate: apiUser.createdAt || new Date().toISOString(),
    //             status: apiUser.approvalStatus === 'approved' ? 'approved' :
    //                 apiUser.approvalStatus === 'pending_l2' ? 'pending_l2' :
    //                     apiUser.approvalStatus === 'rejected' ? 'rejected' : 'pending_l1',
    //             accountStatus: apiUser.isActive ? 'active' : 'closed', // Add account status
    //             approvalStatus: apiUser.approvalStatus, // Add approval status
    //             createdBy: apiUser.createdBy, // Add createdBy field
    //             email: apiUser.email,
    //             phone: apiUser.corporateInfo?.phoneNumber || '',
    //             registrationNumber: apiUser.corporateInfo?.businessRegistrationNumber || '',
    //             entityType: apiUser.corporateInfo?.registrationType || '',
    //             address: apiUser.corporateInfo?.registeredAddress || '',
    //             businessNature: apiUser.corporateInfo?.businessNature || '',
    //             incorporationDate: apiUser.corporateInfo?.dateOfIncorporation || '',
    //             authorizedCapital: apiUser.corporateInfo?.annualRevenue || '',
    //             documents: [],
    //             amlScreening: {
    //                 pepStatus: 'Not PEP',
    //                 sanctionsCheck: 'Clear',
    //                 adverseMedia: 'None Found',
    //                 riskRating: 'Low'
    //             },
    //             userId: apiUser._id // Store the actual user ID for reference
    //         }));
    //     }
    // });

    // Fetch pending approvals for current custodian
    const {
        data: pendingApprovalsData,
        isLoading: pendingApprovalsLoading,
        error: pendingApprovalsError
    } = useQuery({
        queryKey: ['custodian-pending-approvals', user?.id],
        queryFn: () => custodianAPI.getPendingApprovals(),
        enabled: !!user?.id && user?.role === 'custodian' && activeTab === 'pending-approvals'
    });

    // Fetch approval history for current custodian
    const {
        data: approvalHistoryData,
        isLoading: approvalHistoryLoading,
        error: approvalHistoryError
    } = useQuery({
        queryKey: ['custodian-approval-history', user?.id],
        queryFn: () => custodianAPI.getApprovalHistory(),
        enabled: !!user?.id && user?.role === 'custodian' && activeTab === 'approval-history'
    });

    // Mutations for pending request approval/rejection
    const approveRequestMutation = useMutation({
        mutationFn: (request) => custodianAPI.approveRequest(request),
        onSuccess: () => {
            toast.success('Request approved successfully');
            queryClient.invalidateQueries(['custodian-pending-approvals']);
            queryClient.invalidateQueries(['custodian-approval-history']);
            queryClient.invalidateQueries(['custodian-sub-investors']);
            setShowApproveRequestModal(false);
            setSelectedPendingRequest(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to approve request');
        }
    });

    const rejectRequestMutation = useMutation({
        mutationFn: ({ request, reason }) => custodianAPI.rejectRequest(request, reason),
        onSuccess: () => {
            toast.success('Request rejected successfully');
            queryClient.invalidateQueries(['custodian-pending-approvals']);
            queryClient.invalidateQueries(['custodian-approval-history']);
            queryClient.invalidateQueries(['custodian-sub-investors']);
            setShowRejectRequestModal(false);
            setSelectedPendingRequest(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reject request');
        }
    });

    // Mutations for bank account edit approval/rejection
    const approveBankAccountEditMutation = useMutation({
        mutationFn: ({ requestId, comments }) => custodianAPI.approveBankAccountEdit(requestId, comments),
        onSuccess: () => {
            toast.success('Bank account edit approved successfully');
            queryClient.invalidateQueries(['custodian-pending-approvals']);
            queryClient.invalidateQueries(['custodian-approval-history']);
            setShowApproveRequestModal(false);
            setSelectedPendingRequest(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to approve bank account edit');
        }
    });

    const rejectBankAccountEditMutation = useMutation({
        mutationFn: ({ requestId, rejectionReason, comments }) => custodianAPI.rejectBankAccountEdit(requestId, rejectionReason, comments),
        onSuccess: () => {
            toast.success('Bank account edit rejected successfully');
            queryClient.invalidateQueries(['custodian-pending-approvals']);
            queryClient.invalidateQueries(['custodian-approval-history']);
            setShowRejectRequestModal(false);
            setSelectedPendingRequest(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reject bank account edit');
        }
    });

    // Mutations for sub-investor edit approval/rejection
    const approveSubInvestorEditMutation = useMutation({
        mutationFn: ({ requestId, comments }) => custodianAPI.approveSubInvestorEdit(requestId, comments),
        onSuccess: () => {
            toast.success('Sub-investor edit approved successfully');
            queryClient.invalidateQueries(['custodian-pending-approvals']);
            queryClient.invalidateQueries(['custodian-approval-history']);
            queryClient.invalidateQueries(['custodian-sub-investors']);
            setShowApproveRequestModal(false);
            setSelectedPendingRequest(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to approve sub-investor edit');
        }
    });

    const rejectSubInvestorEditMutation = useMutation({
        mutationFn: ({ requestId, rejectionReason, comments }) => custodianAPI.rejectSubInvestorEdit(requestId, rejectionReason, comments),
        onSuccess: () => {
            toast.success('Sub-investor edit rejected successfully');
            queryClient.invalidateQueries(['custodian-pending-approvals']);
            queryClient.invalidateQueries(['custodian-approval-history']);
            queryClient.invalidateQueries(['custodian-sub-investors']);
            setShowRejectRequestModal(false);
            setSelectedPendingRequest(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to reject sub-investor edit');
        }
    });

    // Use the fetched data or fallback to empty array
    const applications = subInvestorsData || [];

    // Helper to remove KYB/KYC prefix patterns from IDs (e.g., KYB-123 -> 123, KYB123 -> 123)
    const formatApplicationId = (rawId) => {
        if (!rawId) return '';
        return String(rawId).replace(/^(KYB|KYC)[-_]?/i, '');
    };

    const filteredApplications = applications.filter(app => {
        const formattedId = formatApplicationId(app.id);
        const matchesSearch = app.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            formattedId.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
        const matchesType = filterType === 'all' || app.applicationType === filterType;
        const result = matchesSearch && matchesStatus && matchesType;

        return result;
    });

    const getStatusBadge = (status) => {
        const statusClasses = {
            pending_review: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
            pending_more_info: 'bg-blue-100 text-blue-800'
        };

        const statusLabels = {
            pending_review: 'Pending Review',
            approved: 'Approved',
            rejected: 'Rejected',
            pending_more_info: 'Pending More Info'
        };

        return {
            class: statusClasses[status] || 'bg-gray-100 text-gray-800',
            label: statusLabels[status] || status
        };
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending_review':
                return <Clock className="w-4 h-4" />;
            case 'approved':
                return <CheckCircle className="w-4 h-4" />;
            case 'rejected':
                return <XCircle className="w-4 h-4" />;
            case 'pending_more_info':
                return <AlertTriangle className="w-4 h-4" />;
            default:
                return <Clock className="w-4 h-4" />;
        }
    };

    const getRiskBadge = (riskRating) => {
        // Ensure riskRating is a valid string with default fallback
        const safeRiskRating = riskRating || 'Low';
        const riskClasses = {
            Low: 'bg-green-100 text-green-800',
            Medium: 'bg-yellow-100 text-yellow-800',
            High: 'bg-red-100 text-red-800'
        };
        return riskClasses[safeRiskRating] || 'bg-gray-100 text-gray-800';
    };

    const getAccountStatusBadge = (accountStatus) => {
        const statusClasses = {
            active: 'bg-green-100 text-green-800',
            closed: 'bg-red-100 text-red-800'
        };

        const statusLabels = {
            active: 'Active',
            closed: 'Closed'
        };

        return {
            class: statusClasses[accountStatus] || 'bg-gray-100 text-gray-800',
            label: statusLabels[accountStatus] || accountStatus
        };
    };

    const getAccountStatusIcon = (accountStatus) => {
        switch (accountStatus) {
            case 'active':
                return <CheckCircle className="w-4 h-4" />;
            case 'closed':
                return <XCircle className="w-4 h-4" />;
            default:
                return <Clock className="w-4 h-4" />;
        }
    };

    const getApprovalStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Approved
                    </span>
                );
            case 'pending_l2':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending L2
                    </span>
                );
            case 'rejected':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Rejected
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                    </span>
                );
        }
    };

    const handleViewApplication = (application) => {
        // Ensure application has complete amlScreening data
        const safeApplication = {
            ...application,
            amlScreening: application.amlScreening || {
                pepStatus: 'Not PEP',
                sanctionsCheck: 'Clear',
                adverseMedia: 'None Found',
                riskRating: 'Low'
            }
        };
        setSelectedApplication(safeApplication);
        setActiveView('details');
        setReviewNotes('');
        setRejectionReason('');
        setRequestMoreInfo('');
        setShowActionsDropdown(null);
    };

    const handleDecision = (decision) => {
        setShowDecisionModal(decision);
    };

    const confirmDecision = () => {
        // In real implementation, this would call API
        setShowDecisionModal(null);
        setActiveView('list');
    };

    const handleActivateDeactivate = (subInvestor, action) => {
        setSelectedSubInvestor(subInvestor);
        setShowActivateModal(action);
    };

    const confirmActivateDeactivate = () => {
        if (!selectedSubInvestor) return;

        if (showActivateModal === 'activate') {
            activateSubInvestorMutation.mutate(selectedSubInvestor.userId);
        } else if (showActivateModal === 'deactivate') {
            deactivateSubInvestorMutation.mutate(selectedSubInvestor.userId);
        }
    };

    const handleEditSubInvestor = (subInvestor) => {
        console.log('SubInvestor data for editing:', subInvestor);
        console.log('New fields data:', {
            ssaName: subInvestor.ssaName,
            ssaAccountNumber: subInvestor.ssaAccountNumber,
            custodyAccountNumber: subInvestor.custodyAccountNumber,
            residencyStatus: subInvestor.residencyStatus,
            lei: subInvestor.lei
        });

        setSelectedSubInvestor(subInvestor);
        setEditForm({
            name: subInvestor.applicantName || '',
            email: subInvestor.email || '',
            phone: subInvestor.phone || '',
            registrationNumber: subInvestor.registrationNumber || '',
            entityType: subInvestor.entityType || '',
            address: subInvestor.address || '',
            incorporationDate: subInvestor.incorporationDate || '',
            ssaName: subInvestor.ssaName || '',
            ssaAccountNumber: subInvestor.ssaAccountNumber || '',
            custodyAccountNumber: subInvestor.custodyAccountNumber || '',
            residencyStatus: subInvestor.residencyStatus || '',
            lei: subInvestor.lei || ''
        });
        setShowEditModal(true);
    };

    const handleManageWallets = async (subInvestor) => {
        setSelectedSubInvestor(subInvestor);
        setShowActionsDropdown(null);

        try {
            const response = await custodianAPI.getSubInvestorWallets(subInvestor.userId);
            if (response.data.success) {
                setSubInvestorWallets(response.data.wallets);
                setShowWalletModal(true);
            } else {
                toast.error('Failed to load wallet information');
            }
        } catch (error) {
            toast.error('Failed to load wallet information');
        }
    };

    const handleEditBankAccount = (wallet) => {
        setEditingWallet(wallet);
        setEditBankAccountForm({
            bankName: wallet.bankAccountDetails.bankName || '',
            accountName: wallet.bankAccountDetails.accountName || '',
            accountNumber: wallet.bankAccountDetails.accountNumber || '',
            accountCategory: wallet.bankAccountDetails.accountCategory || 'current',
            swiftBic: wallet.bankAccountDetails.swiftBic || '',
            branchCode: wallet.bankAccountDetails.branchCode || ''
        });
    };

    const handleEditBankAccountFormChange = (field, value) => {
        setEditBankAccountForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleUpdateBankAccount = async () => {
        if (!editingWallet || !selectedSubInvestor) return;

        // Validate required fields
        if (!editBankAccountForm.bankName || !editBankAccountForm.accountName || !editBankAccountForm.accountNumber || !editBankAccountForm.accountCategory) {
            toast.error('Please fill in all required fields (Bank Name, Account Name, Account Number, and Account Category)');
            return;
        }

        try {
            const response = await custodianAPI.requestBankAccountEdit(
                selectedSubInvestor.userId,
                editingWallet._id,
                editBankAccountForm
            );

            if (response.data.success) {
                toast.success('Bank account edit request submitted for L2 approval');

                // Invalidate pending approvals query to refresh the list
                queryClient.invalidateQueries(['custodian-pending-approvals', user?.id]);

                setEditingWallet(null);
                setEditBankAccountForm({});
            } else {
                toast.error('Failed to submit bank account edit request');
            }
        } catch (error) {
            toast.error('Failed to submit bank account edit request');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Wallet address copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy wallet address');
        });
    };

    const handleResetPassword = (subInvestor) => {
        setSelectedSubInvestor(subInvestor);
        setShowResetPasswordModal(true);
        setResetPasswordResult(null);
        setShowActionsDropdown(null);
    };

    const confirmResetPassword = () => {
        if (!selectedSubInvestor) return;
        resetPasswordMutation.mutate(selectedSubInvestor.userId);
    };

    const handleDeleteSubInvestor = (subInvestor) => {
        setSelectedSubInvestor(subInvestor);
        setShowDeleteModal(true);
        setShowActionsDropdown(null);
    };

    const confirmDeleteSubInvestor = () => {
        if (!selectedSubInvestor) return;
        deleteSubInvestorMutation.mutate(selectedSubInvestor.userId);
    };

    const toggleActionsDropdown = (subInvestorId) => {
        if (showActionsDropdown === subInvestorId) {
            setShowActionsDropdown(null);
        } else {
            const button = actionButtonRefs.current[subInvestorId];
            if (button) {
                const rect = button.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

                setDropdownPosition({
                    top: rect.bottom + scrollTop + 8, // 8px gap below button
                    left: rect.right + scrollLeft - 192 // 192px = w-48 (dropdown width)
                });
            }
            setShowActionsDropdown(subInvestorId);
        }
    };

    const handleApproveL2 = (subInvestor) => {
        setSelectedSubInvestor(subInvestor);
        setShowApproveL2Modal(true);
        setShowActionsDropdown(null);
    };

    const confirmApproveL2 = () => {
        if (!selectedSubInvestor) return;
        approveL2Mutation.mutate({
            subInvestorId: selectedSubInvestor.userId,
            comments: approvalComments
        });
    };

    const handleRejectSubInvestor = (subInvestor) => {
        setSelectedSubInvestor(subInvestor);
        setShowRejectModal(true);
        setShowActionsDropdown(null);
    };

    const confirmRejectSubInvestor = () => {
        if (!selectedSubInvestor || !approvalRejectionReason.trim()) return;
        rejectSubInvestorMutation.mutate({
            subInvestorId: selectedSubInvestor.userId,
            rejectionReason: approvalRejectionReason,
            comments: approvalRejectionComments
        });
    };

    // Click outside handler to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showActionsDropdown && !event.target.closest('.actions-dropdown')) {
                setShowActionsDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showActionsDropdown]);

    const handleEditFormChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const submitEditForm = async () => {
        if (!selectedSubInvestor) return;

        // Validate required fields
        if (!editForm.name || !editForm.email || !editForm.registrationNumber || !editForm.ssaName || !editForm.ssaAccountNumber || !editForm.custodyAccountNumber || !editForm.residencyStatus || !editForm.lei) {
            toast.error('Please fill in all required fields (Company Name, Email Address, Registration Number, SSA Name, SSA Account Number, Custody Account Number, Residency Status, and LEI)');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editForm.email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        // Prepare update data
        const updateData = {
            email: editForm.email,
            corporateInfo: {
                companyName: editForm.name,
                businessRegistrationNumber: editForm.registrationNumber,
                phoneNumber: editForm.phone || '',
                registeredAddress: editForm.address || '',
                registrationType: editForm.entityType || '',
                dateOfIncorporation: editForm.incorporationDate || '',
                ssaName: editForm.ssaName,
                ssaAccountNumber: editForm.ssaAccountNumber,
                custodyAccountNumber: editForm.custodyAccountNumber,
                residencyStatus: editForm.residencyStatus,
                lei: editForm.lei
            }
        };

        try {
            const response = await custodianAPI.requestSubInvestorEdit(selectedSubInvestor.userId, updateData);

            if (response.data.success) {
                toast.success('Sub-investor edit request submitted for L2 approval');

                // Invalidate pending approvals query to refresh the list
                queryClient.invalidateQueries(['custodian-pending-approvals', user?.id]);

                setShowEditModal(false);
                setSelectedSubInvestor(null);
            } else {
                toast.error('Failed to submit sub-investor edit request');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to submit sub-investor edit request';
            toast.error(errorMessage);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Handlers for pending request approval/rejection
    const handleApproveRequest = (request) => {
        setSelectedPendingRequest(request);
        setShowApproveRequestModal(true);
    };

    const handleRejectRequest = (request) => {
        setSelectedPendingRequest(request);
        setShowRejectRequestModal(true);
    };

    const confirmApproveRequest = () => {
        if (selectedPendingRequest) {
            if (selectedPendingRequest.actionType === 'edit_bank_account') {
                approveBankAccountEditMutation.mutate({
                    requestId: selectedPendingRequest._id,
                    comments: approvalComments
                });
            } else if (selectedPendingRequest.actionType === 'edit_sub_investor') {
                approveSubInvestorEditMutation.mutate({
                    requestId: selectedPendingRequest._id,
                    comments: approvalComments
                });
            } else {
                approveRequestMutation.mutate(selectedPendingRequest);
            }
        }
    };

    const confirmRejectRequest = () => {
        if (selectedPendingRequest) {
            if (selectedPendingRequest.actionType === 'edit_bank_account') {
                rejectBankAccountEditMutation.mutate({
                    requestId: selectedPendingRequest._id,
                    rejectionReason: approvalRejectionReason,
                    comments: approvalRejectionComments
                });
            } else if (selectedPendingRequest.actionType === 'edit_sub_investor') {
                rejectSubInvestorEditMutation.mutate({
                    requestId: selectedPendingRequest._id,
                    rejectionReason: approvalRejectionReason,
                    comments: approvalRejectionComments
                });
            } else {
                rejectRequestMutation.mutate({
                    request: selectedPendingRequest,
                    reason: 'Request rejected'
                });
            }
        }
    };

    const handleNewInvestorFormChange = (field, value) => {
        setNewInvestorForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle bank account type selection
    const handleAccountTypeSelection = (accountType, isSelected) => {
        if (isSelected) {
            setSelectedAccountTypes(prev => [...prev, accountType]);
            // Initialize form for new account type
            setBankAccountForms(prev => ({
                ...prev,
                [accountType]: {
                    bankName: '',
                    accountName: '',
                    accountNumber: '',
                    accountCategory: 'current',
                    swiftBic: '',
                    branchCode: '',
                    shariahComplianceDocument: null
                }
            }));
        } else {
            setSelectedAccountTypes(prev => prev.filter(type => type !== accountType));
            // Remove form for deselected account type
            setBankAccountForms(prev => {
                const newForms = { ...prev };
                delete newForms[accountType];
                return newForms;
            });
        }
    };

    // Handle bank account form field changes
    const handleBankAccountFormChange = (accountType, field, value) => {
        setBankAccountForms(prev => ({
            ...prev,
            [accountType]: {
                ...prev[accountType],
                [field]: value
            }
        }));
    };

    // Handle file upload for Shariah compliance document
    const handleShariahDocumentUpload = (accountType, file) => {
        setBankAccountForms(prev => ({
            ...prev,
            [accountType]: {
                ...prev[accountType],
                shariahComplianceDocument: file
            }
        }));
    };

    // Document handling functions - currently unused but kept for future implementation
    // const handleDocumentUpload = (file, documentType) => {
    //     const newDocument = {
    //         id: Date.now(),
    //         name: file.name,
    //         type: documentType,
    //         file: file,
    //         status: 'uploaded'
    //     };
    //     setUploadedDocuments(prev => [...prev, newDocument]);
    // };

    // const removeDocument = (documentId) => {
    //     setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
    // };

    const submitNewInvestorApplication = () => {
        // Validate required fields - Full Name, Email Address, Initial Password, Registration/Account Number, SSA Name, SSA Account Number, Custody Account Number, Residency Status, and LEI are mandatory
        if (!newInvestorForm.name || !newInvestorForm.email || !newInvestorForm.initialPassword || !newInvestorForm.registrationNumber || !newInvestorForm.ssaName || !newInvestorForm.ssaAccountNumber || !newInvestorForm.custodyAccountNumber || !newInvestorForm.residencyStatus || !newInvestorForm.lei) {
            toast.error('Please fill in all required fields (Full Name, Email Address, Initial Password, Registration/Account Number, SSA Name, SSA Account Number, Custody Account Number, Residency Status, and LEI)');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newInvestorForm.email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        // Validate password strength (minimum requirements)
        if (newInvestorForm.initialPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        // Validate bank account information
        if (selectedAccountTypes.length === 0) {
            toast.error('Please select at least one bank account type');
            return;
        }

        // Validate bank account forms
        for (const accountType of selectedAccountTypes) {
            const form = bankAccountForms[accountType];
            if (!form.bankName || !form.accountName || !form.accountNumber) {
                toast.error(`Please fill in all required fields for ${accountType} bank account`);
                return;
            }
        }

        // Prepare bank accounts data
        const bankAccounts = selectedAccountTypes.map(accountType => {
            const form = bankAccountForms[accountType];
            return {
                accountType,
                bankName: form.bankName,
                accountName: form.accountName,
                accountNumber: form.accountNumber,
                accountCategory: form.accountCategory,
                swiftBic: form.swiftBic || '',
                branchCode: form.branchCode || '',
                shariahComplianceDocument: form.shariahComplianceDocument
            };
        });

        // Prepare sub-investor data for the custodian API
        const subInvestorData = {
            email: newInvestorForm.email,
            password: newInvestorForm.initialPassword,
            corporateInfo: {
                companyName: newInvestorForm.name,
                businessRegistrationNumber: newInvestorForm.registrationNumber,
                phoneNumber: newInvestorForm.phone || '',
                address: newInvestorForm.address || '',
                entityType: newInvestorForm.entityType || '',
                incorporationDate: newInvestorForm.incorporationDate || '',
                ssaName: newInvestorForm.ssaName,
                ssaAccountNumber: newInvestorForm.ssaAccountNumber,
                custodyAccountNumber: newInvestorForm.custodyAccountNumber,
                residencyStatus: newInvestorForm.residencyStatus,
                lei: newInvestorForm.lei
            },
            bankAccounts
        };

        createInvestorMutation.mutate(subInvestorData);
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await custodianAPI.downloadTemplate();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'sub-investor-template.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('Template downloaded successfully!');
        } catch (error) {
            toast.error('Failed to download template. Please try again.');
        }
    };

    const handleBatchUpload = async () => {
        if (!batchFile) {
            toast.error('Please select a file to upload');
            return;
        }

        setIsUploading(true);
        setBatchUploadResults(null);

        try {
            // Read the CSV file
            const reader = new FileReader();
            reader.onload = (e) => {
                const csvData = e.target.result;

                // Submit the CSV data to the API
                batchUploadMutation.mutate(csvData);
            };
            reader.readAsText(batchFile);
        } catch (error) {
            setIsUploading(false);
            toast.error('Failed to read the CSV file. Please try again.');
        }
    };

    // Export currently loaded sub-investors (respecting current filters) to CSV.
    // Uses new tab-delimited format matching the upload template
    const handleExportSubInvestorsCSV = () => {
        try {
            const exportData = (typeof filteredApplications !== 'undefined' && Array.isArray(filteredApplications)) ? filteredApplications : applications;
            if (!exportData || exportData.length === 0) {
                toast.warning('No sub-investors to export.');
                return;
            }

            const headers = [
                'Master Acct No.',
                'Master Acct Name',
                'Custody Acct No.',
                'Custody Acct Name',
                'Email',
                'Custody Acct Status',
                'Country',
                'Account open date',
                'Domicile',
                'CDS Acct No.',
                'CDS Acct Name.',
                'Cash Acc No CORPORATE ACTION.',
                'Cash Acc Name CORPORATE ACTION',
                'Cash Acc No LOCAL TRADE.',
                'Cash Acc Name LOCAL TRADE',
                'Cash Acc No GLOBAL TRADE.',
                'Cash Acc Name GLOBAL TRADE',
                'Cash Acc No BILLING.',
                'Cash Acc Name BILLING',
                'Maker',
                'Maker Date',
                'Checker',
                'Checker Date',
                'LEI No',
                'Wallet No'
            ];

            const escape = (value) => {
                if (value === null || value === undefined) return '';
                const str = String(value).replace(/\r?\n|\r/g, ' ');
                if (/[\t"]/.test(str)) {
                    return '"' + str.replace(/"/g, '""') + '"';
                }
                return str;
            };

            const rows = exportData.map(app => {
                // Extract wallet address (conventional wallet preferred)
                const walletNo = (app.conventionalWalletAddress && app.conventionalWalletAddress !== '-'
                    ? app.conventionalWalletAddress
                    : (app.islamicWalletAddress && app.islamicWalletAddress !== '-'
                        ? app.islamicWalletAddress
                        : ''));

                // Format dates
                const accountOpenDate = app.incorporationDate ? new Date(app.incorporationDate).toISOString().split('T')[0] : '';
                const createdDate = app.submissionDate ? new Date(app.submissionDate).toISOString().split('T')[0] : '';

                // Determine custody account status
                const custodyStatus = app.accountStatus === 'active' ? 'active' : 'inactive';

                return [
                    app.registrationNumber || app.ssaAccountNumber || '',
                    app.applicantName || '',
                    app.custodyAccountNumber || '',
                    app.applicantName || '',
                    app.email || '',
                    custodyStatus,
                    app.address || '',
                    accountOpenDate,
                    app.residencyStatus || '',
                    '', // CDS Acct No.
                    '', // CDS Acct Name.
                    '', // Cash Acc No CORPORATE ACTION.
                    '', // Cash Acc Name CORPORATE ACTION
                    '', // Cash Acc No LOCAL TRADE.
                    '', // Cash Acc Name LOCAL TRADE
                    '', // Cash Acc No GLOBAL TRADE.
                    '', // Cash Acc Name GLOBAL TRADE
                    '', // Cash Acc No BILLING.
                    '', // Cash Acc Name BILLING
                    '', // Maker
                    createdDate, // Maker Date
                    '', // Checker
                    '', // Checker Date
                    app.lei || '',
                    walletNo
                ].map(escape).join('\t');
            });

            const csvContent = [headers.join('\t'), ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
            link.download = `sub-investors-export-${timestamp}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success(`Exported ${exportData.length} sub-investor${exportData.length === 1 ? '' : 's'} to CSV.`);
        } catch (err) {
            console.error('CSV export failed', err);
            toast.error('Failed to export sub-investors.');
        }
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
                {/* Page Header - Show for all main tabs */}
                {(activeTab === 'sub-investors' || activeTab === 'pending-approvals' || activeTab === 'approval-history') && activeView === 'list' && (
                    <>
                        <div className="mb-8">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900">
                                        {activeTab === 'sub-investors' ? 'Sub Investors' :
                                            activeTab === 'pending-approvals' ? 'Pending Approvals' :
                                                activeTab === 'approval-history' ? 'Approval History' : 'Sub Investors'}
                                    </h1>
                                    <p className="mt-2 text-gray-600">
                                        {activeTab === 'sub-investors' ? 'Create subinvestors account for platform onboarding' :
                                            activeTab === 'pending-approvals' ? 'Review and approve pending sub-investor actions including create, activate, deactivate, and delete requests.' :
                                                activeTab === 'approval-history' ? 'View complete history of all sub-investor approval decisions and actions.' :
                                                    'Create subinvestors account for platform onboarding'}
                                    </p>
                                </div>
                                {activeTab === 'sub-investors' && (
                                    <div className="flex space-x-3">
                                        <button
                                            onClick={true ? () => setActiveView('new-investor') : undefined}

                                            className="btn-primary"
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Create New Sub Investor
                                        </button>
                                        <button
                                            onClick={true ? () => setActiveView('upload-batch') : undefined}

                                            className={`btn-secondary `}
                                        >
                                            <FileText className="w-4 h-4 mr-2" />
                                            Upload CSV
                                        </button>
                                        <button
                                            onClick={handleExportSubInvestorsCSV}
                                            disabled={applications.length === 0}
                                            className="btn-secondary"
                                            title={applications.length === 0 ? 'No sub-investors to export' : 'Export visible sub-investors to CSV'}
                                        >
                                            <FileText className="w-4 h-4 mr-2" />
                                            Export CSV
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tabs - Show for all main tabs */}
                        <div className="mb-6 border-b border-gray-200">
                            <nav className="flex -mb-px space-x-8">
                                <button
                                    onClick={() => {
                                        setActiveTab('sub-investors');
                                        setActiveView('list');
                                    }}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'sub-investors'
                                        ? 'border-primary-500 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    My Sub-Investors
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab('pending-approvals');
                                        setActiveView('list');
                                    }}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'pending-approvals'
                                        ? 'border-primary-500 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    Pending Approvals
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab('approval-history');
                                        setActiveView('list');
                                    }}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'approval-history'
                                        ? 'border-primary-500 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    Approval History
                                </button>
                            </nav>
                        </div>
                    </>
                )}

                {/* Tab Content */}
                {activeTab === 'sub-investors' && activeView === 'list' ? (
                    <>
                        {/* Filters */}
                        <div className="grid grid-cols-1 gap-4 mb-8 lg:grid-cols-5">
                            {/* Search Card - 60% width (3 out of 5 columns) */}
                            <div className="card lg:col-span-4">
                                <div className="card-body">
                                    <div className="relative">
                                        <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-dark-gray" />
                                        <input
                                            type="text"
                                            placeholder="Search by name or ID..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-10 form-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Status Filter Card - 20% width (1 out of 5 columns) */}
                            <div className="card lg:col-span-1">
                                <div className="card-body">
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="approved">Approved</option>
                                        <option value="pending_l1">Pending</option>
                                        <option value="pending_l2">Pending L2</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>
                            </div>


                        </div>

                        {/* Loading State */}
                        {subInvestorsLoading && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                                    <span className="ml-3 text-gray-600">Loading sub-investors...</span>
                                </div>
                            </div>
                        )}

                        {/* Error State */}
                        {subInvestorsError && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <div className="py-12 text-center">
                                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                                    <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Data</h3>
                                    <p className="mb-4 text-gray-600">
                                        Failed to load sub-investors: {subInvestorsError.message || 'Unknown error'}
                                    </p>
                                    <button
                                        onClick={() => refetchSubInvestors()}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Applications Table */}
                        {!subInvestorsLoading && !subInvestorsError && (
                            <div className="overflow-x-auto bg-white rounded-lg shadow">
                                <div className="px-4 py-5 sm:px-6">
                                    <h3 className="text-lg font-medium leading-6 text-gray-900">Sub Investors</h3>
                                </div>
                                <table className="table">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="table-header-cell">
                                                User ID
                                            </th>
                                            <th className="table-header-cell">
                                                User Name
                                            </th>
                                            <th className="table-header-cell">
                                                LEI
                                            </th>
                                            <th className="table-header-cell">
                                                Bank Account
                                            </th>
                                            <th className="table-header-cell">
                                                Wallet Address (Conventional)
                                            </th>
                                            <th className="table-header-cell">
                                                Wallet Address (Islamic)
                                            </th>
                                            <th className="table-header-cell">
                                                Submission Date
                                            </th>
                                            <th className="table-header-cell">
                                                Account Status
                                            </th>
                                            <th className="table-header-cell">
                                                Approval Status
                                            </th>

                                            <th className="table-header-cell">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredApplications.map((application) => {
                                            // Ensure application has required fields with defaults
                                            const safeApplication = {
                                                ...application,
                                                amlScreening: application.amlScreening || {
                                                    pepStatus: 'Not PEP',
                                                    sanctionsCheck: 'Clear',
                                                    adverseMedia: 'None Found',
                                                    riskRating: 'Low'
                                                }
                                            };
                                            return (
                                                <tr key={safeApplication.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-mono text-sm text-gray-900">
                                                            {(safeApplication._id || safeApplication.id)?.replace(/^(KYB-|KYC-)/, '') || (safeApplication._id || safeApplication.id)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {safeApplication.applicantName}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {safeApplication.email || safeApplication.applicantEmail || '-'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="font-mono text-sm text-gray-900">
                                                            {safeApplication.lei || '-'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="space-y-1">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {safeApplication.conventionalBankAccount?.bankName !== '-' ? safeApplication.conventionalBankAccount.bankName :
                                                                    safeApplication.islamicBankAccount?.bankName !== '-' ? safeApplication.islamicBankAccount.bankName : '-'}
                                                            </div>
                                                            <div className="font-mono text-sm text-gray-500">
                                                                {safeApplication.conventionalBankAccount?.accountNumber !== '-' ? safeApplication.conventionalBankAccount.accountNumber :
                                                                    safeApplication.islamicBankAccount?.accountNumber !== '-' ? safeApplication.islamicBankAccount.accountNumber : '-'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="font-mono text-sm text-gray-900" title={safeApplication.conventionalWalletAddress}>
                                                                {safeApplication.conventionalWalletAddress && safeApplication.conventionalWalletAddress !== '-'
                                                                    ? `${safeApplication.conventionalWalletAddress.slice(0, 10)}...${safeApplication.conventionalWalletAddress.slice(-8)}`
                                                                    : safeApplication.conventionalWalletAddress}
                                                            </div>
                                                            {safeApplication.conventionalWalletAddress !== '-' && (
                                                                <button
                                                                    onClick={() => {
                                                                        if (safeApplication.conventionalWalletAddress !== '-') {
                                                                            navigator.clipboard.writeText(safeApplication.conventionalWalletAddress);
                                                                            toast.success('Conventional Wallet Address copied to clipboard!');
                                                                        }
                                                                    }}
                                                                    className="text-gray-400 transition-colors hover:text-gray-600"
                                                                    title="Copy Conventional Wallet Address"
                                                                >
                                                                    <Copy className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="font-mono text-sm text-gray-900" title={safeApplication.islamicWalletAddress}>
                                                                {safeApplication.islamicWalletAddress && safeApplication.islamicWalletAddress !== '-'
                                                                    ? `${safeApplication.islamicWalletAddress.slice(0, 10)}...${safeApplication.islamicWalletAddress.slice(-8)}`
                                                                    : safeApplication.islamicWalletAddress}
                                                            </div>
                                                            {safeApplication.islamicWalletAddress !== '-' && (
                                                                <button
                                                                    onClick={() => {
                                                                        if (safeApplication.islamicWalletAddress !== '-') {
                                                                            navigator.clipboard.writeText(safeApplication.islamicWalletAddress);
                                                                            toast.success('Islamic Wallet Address copied to clipboard!');
                                                                        }
                                                                    }}
                                                                    className="text-gray-400 transition-colors hover:text-gray-600"
                                                                    title="Copy Islamic Wallet Address"
                                                                >
                                                                    <Copy className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                        {formatDate(safeApplication.submissionDate)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {(() => {
                                                            const accountStatusBadge = getAccountStatusBadge(safeApplication.accountStatus);
                                                            return (
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${accountStatusBadge.class}`}>
                                                                    {getAccountStatusIcon(safeApplication.accountStatus)}
                                                                    <span className="ml-1">{accountStatusBadge.label}</span>
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getApprovalStatusBadge(safeApplication.approvalStatus)}
                                                    </td>

                                                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                                                        <div className="relative">
                                                            <button
                                                                ref={(el) => actionButtonRefs.current[safeApplication.id] = el}
                                                                onClick={true ? () => toggleActionsDropdown(safeApplication.id) : undefined}

                                                                className={`inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 `}
                                                            >
                                                                <MoreHorizontal className="w-3 h-3 mr-1" />
                                                                Actions
                                                            </button>

                                                            {/* Actions Dropdown moved to portal */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {filteredApplications.length === 0 && (
                                    <div className="py-12 text-center">
                                        <UserCheck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                        <h3 className="mb-2 text-lg font-medium text-gray-900">No applications found</h3>
                                        <p className="text-gray-600">
                                            {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                                                ? 'Try adjusting your search or filter criteria.'
                                                : 'No sub-investors created yet. Use the "Create New Sub Investor" button to create one'
                                            }
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : activeTab === 'pending-approvals' ? (
                    <>
                        {/* Loading State */}
                        {pendingApprovalsLoading && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                                    <span className="ml-3 text-gray-600">Loading pending approvals...</span>
                                </div>
                            </div>
                        )}

                        {/* Error State */}
                        {pendingApprovalsError && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <div className="py-12 text-center">
                                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                                    <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Data</h3>
                                    <p className="mb-4 text-gray-600">
                                        Failed to load pending approvals: {pendingApprovalsError.message || 'Unknown error'}
                                    </p>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Pending Approvals Table */}
                        {!pendingApprovalsLoading && !pendingApprovalsError && (
                            <div className="overflow-x-auto bg-white rounded-lg shadow">
                                <div className="px-4 py-5 sm:px-6">
                                    <h3 className="text-lg font-medium leading-6 text-gray-900">Pending Approvals</h3>
                                </div>
                                <table className="table">
                                    <thead className="table-header">
                                        <tr>
                                            <th className="table-header-cell">
                                                Request Type
                                            </th>
                                            <th className="table-header-cell">
                                                Target
                                            </th>
                                            <th className="table-header-cell">
                                                Initiated By
                                            </th>
                                            <th className="table-header-cell">
                                                Submitted
                                            </th>
                                            <th className="table-header-cell">
                                                Status
                                            </th>
                                            <th className="table-header-cell">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {(pendingApprovalsData?.data?.data || []).map((request) => {
                                            const getRequestTypeLabel = (actionType) => {
                                                switch (actionType) {
                                                    case 'create_sub_investor': return 'Create Sub-Investor';
                                                    case 'activate':
                                                    case 'activate_sub_investor': return 'Activate Sub-Investor';
                                                    case 'deactivate':
                                                    case 'deactivate_sub_investor': return 'Deactivate Sub-Investor';
                                                    case 'delete':
                                                    case 'delete_sub_investor': return 'Delete Sub-Investor';
                                                    case 'edit_bank_account': return 'Edit Bank Account';
                                                    case 'edit_sub_investor': return 'Edit Sub-Investor';
                                                    default: return actionType;
                                                }
                                            };

                                            const getStatusBadge = (status) => {
                                                switch (status) {
                                                    case 'pending_l1':
                                                        return <span className="inline-flex px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full">Pending L1</span>;
                                                    case 'pending_l2':
                                                        return <span className="inline-flex px-2 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full">Pending L2</span>;
                                                    default:
                                                        return <span className="inline-flex px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">{status}</span>;
                                                }
                                            };

                                            return (
                                                <tr key={request._id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {getRequestTypeLabel(request.actionType)}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {request.type}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {request.targetData?.name || 'N/A'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {request.targetData?.email || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                        {request.initiatedBy?.email || 'System'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                                                        {formatDate(request.createdAt)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {getStatusBadge(request.status)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                                                        <div className="flex space-x-2">
                                                            {(() => {
                                                                // Check if current user is the initiator
                                                                const isInitiator = request.initiatedBy?._id === user?.id ||
                                                                    request.initiatedBy?._id === user?._id;

                                                                if (isInitiator) {
                                                                    return null;
                                                                }

                                                                return (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleApproveRequest(request)}

                                                                            className={`btn-primary btn-sm `}
                                                                        >
                                                                            {approveRequestMutation.isLoading ? 'Approving...' : 'Approve'}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRejectRequest(request)}

                                                                            className={`btn-secondary btn-sm `}
                                                                        >
                                                                            {rejectRequestMutation.isLoading ? 'Rejecting...' : 'Reject'}
                                                                        </button>
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {(!pendingApprovalsData?.data?.data || pendingApprovalsData.data.data.length === 0) && (
                                    <div className="py-12 text-center">
                                        <UserCheck className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                        <h3 className="mb-2 text-lg font-medium text-gray-900">No pending approvals</h3>
                                        <p className="text-gray-600">
                                            There are no sub-investor actions waiting for your approval.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : activeTab === 'approval-history' ? (
                    <>
                        {/* Loading State */}
                        {approvalHistoryLoading && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                                    <span className="ml-3 text-gray-600">Loading approval history...</span>
                                </div>
                            </div>
                        )}

                        {/* Error State */}
                        {approvalHistoryError && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <div className="flex items-center justify-center py-12">
                                    <div className="text-center">
                                        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                                        <h3 className="mb-2 text-lg font-medium text-gray-900">Error Loading Data</h3>
                                        <p className="text-gray-600">Unable to load approval history. Please try again.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Approval History Content */}
                        {!approvalHistoryLoading && !approvalHistoryError && (
                            <div className="bg-white rounded-lg shadow">
                                {approvalHistoryData?.data?.data?.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <div className="px-4 py-5 sm:px-6">
                                            <h3 className="text-lg font-medium leading-6 text-gray-900">Approval History</h3>
                                        </div>
                                        <table className="table">
                                            <thead className="table-header">
                                                <tr>
                                                    <th className="table-header-cell">
                                                        Request Type
                                                    </th>
                                                    <th className="table-header-cell">
                                                        Sub-Investor
                                                    </th>
                                                    <th className="table-header-cell">
                                                        Initiated By
                                                    </th>
                                                    <th className="table-header-cell">
                                                        Initiated At
                                                    </th>
                                                    <th className="table-header-cell">
                                                        Approved/Rejected By
                                                    </th>
                                                    <th className="table-header-cell">
                                                        Action Date
                                                    </th>
                                                    <th className="table-header-cell">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {(approvalHistoryData?.data?.data || []).map((request) => (
                                                    <tr key={request._id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {request.actionType === 'create_sub_investor' ? 'Create Sub-Investor' :
                                                                    request.actionType === 'l1_approval' ? 'L1 Approval' :
                                                                        request.actionType === 'l2_approval' ? 'L2 Approval' :
                                                                            request.actionType === 'activate' || request.actionType === 'activate_sub_investor' ? 'Activate Sub-Investor' :
                                                                                request.actionType === 'deactivate' || request.actionType === 'deactivate_sub_investor' ? 'Deactivate Sub-Investor' :
                                                                                    request.actionType === 'delete' || request.actionType === 'delete_sub_investor' ? 'Delete Sub-Investor' :
                                                                                        request.actionType === 'edit_bank_account' ? 'Edit Bank Account' :
                                                                                            request.actionType === 'edit_sub_investor' ? 'Edit Sub-Investor' :
                                                                                                request.actionType}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {request.targetData?.name || request.targetData?.corporateInfo?.companyName || 'N/A'}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {request.targetData?.email || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {request.initiatedBy?.email || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                            {new Date(request.createdAt).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {request.approvedBy?.email || request.rejectedBy?.email || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                            {request.updatedAt ? new Date(request.updatedAt).toLocaleDateString() : 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${request.status === 'approved'
                                                                ? 'bg-green-100 text-green-800'
                                                                : request.status === 'rejected'
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {request.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="p-6 text-center">
                                        <Shield className="w-12 h-12 mx-auto text-gray-400" />
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No approval history</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            No approval history available yet.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : activeView === 'new-investor' ? (
                    /* New Investor Form */
                    <div className="space-y-6">
                        {/* Header */}
                        <div className="rounded-lg">
                            <div className="flex items-center">
                                <button
                                    onClick={() => setActiveView('list')}
                                    className="p-2 mr-4 text-gray-400 transition-colors rounded-md hover:text-gray-600 hover:bg-gray-100"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">New Sub Investor Onboarding</h2>
                                    <p className="mt-1 text-sm text-gray-500">Fill in investor information. Required fields are: <strong>Full Name</strong>, <strong>Email Address</strong>, <strong>Initial Password</strong>, <strong>Registration/Account Number</strong>, <strong>SSA Name</strong>, <strong>SSA Account Number</strong>, <strong>Custody Account Number</strong>, <strong>Residency Status</strong>, and <strong>LEI</strong>. All other fields are optional.</p>
                                </div>
                            </div>
                        </div>



                        {/* Basic Information */}
                        <div className="bg-white border rounded-lg border-light-gray">
                            <div className="px-6 pt-6">
                                <h3 className="mb-4 text-lg font-medium text-gray-900">Company Information</h3>
                            </div>
                            <div className="border-b border-light-gray"></div>

                            {/* Required Fields */}
                            <div className="px-6 pt-6 pb-6">
                                <div className="mb-6">
                                    <h4 className="mb-3 font-medium text-gray-800 text-md">Required Fields</h4>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newInvestorForm.name}
                                                onChange={(e) => handleNewInvestorFormChange('name', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter company name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                value={newInvestorForm.email}
                                                onChange={(e) => handleNewInvestorFormChange('email', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter email address"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Initial Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                value={newInvestorForm.initialPassword}
                                                onChange={(e) => handleNewInvestorFormChange('initialPassword', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter initial password"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Registration/Account Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newInvestorForm.registrationNumber}
                                                onChange={(e) => handleNewInvestorFormChange('registrationNumber', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter registration/account number"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                SSA Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newInvestorForm.ssaName}
                                                onChange={(e) => handleNewInvestorFormChange('ssaName', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter SSA name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                SSA Account Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newInvestorForm.ssaAccountNumber}
                                                onChange={(e) => handleNewInvestorFormChange('ssaAccountNumber', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter SSA account number"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Custody Account Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newInvestorForm.custodyAccountNumber}
                                                onChange={(e) => handleNewInvestorFormChange('custodyAccountNumber', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter custody account number"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Residency Status <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={newInvestorForm.residencyStatus}
                                                onChange={(e) => handleNewInvestorFormChange('residencyStatus', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                required
                                            >
                                                <option value="">Select residency status</option>
                                                <option value="resident">Resident</option>
                                                <option value="non-resident">Non-Resident</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                LEI (Legal Entity Identifier) <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={newInvestorForm.lei}
                                                onChange={(e) => handleNewInvestorFormChange('lei', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter LEI"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Optional Fields */}
                                <div>
                                    <h4 className="mb-3 font-medium text-gray-800 text-md">Optional Fields</h4>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Phone Number <span className="text-sm text-gray-400">(Optional)</span>
                                            </label>
                                            <input
                                                type="tel"
                                                value={newInvestorForm.phone}
                                                onChange={(e) => handleNewInvestorFormChange('phone', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Entity Type <span className="text-sm text-gray-400">(Optional)</span>
                                            </label>
                                            <select
                                                value={newInvestorForm.entityType}
                                                onChange={(e) => handleNewInvestorFormChange('entityType', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            >
                                                <option value="">Select entity type</option>
                                                <option value="Private Limited">Private Limited Company</option>
                                                <option value="Public Limited">Public Limited Company</option>
                                                <option value="Partnership">Partnership</option>
                                                <option value="Trust">Trust</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Incorporation Date <span className="text-sm text-gray-400">(Optional)</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={newInvestorForm.incorporationDate}
                                                onChange={(e) => handleNewInvestorFormChange('incorporationDate', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Address <span className="text-sm text-gray-400">(Optional)</span>
                                            </label>
                                            <textarea
                                                value={newInvestorForm.address}
                                                onChange={(e) => handleNewInvestorFormChange('address', e.target.value)}
                                                rows={3}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter full address"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bank Account Information */}
                        <div className="bg-white border rounded-lg border-light-gray">
                            <div className="px-6 pt-6">
                                <h3 className="mb-4 text-lg font-medium text-gray-900">Bank Account Information</h3>
                            </div>
                            <div className="border-b border-light-gray"></div>

                            {/* Bank Account Type Selection */}
                            <div className="px-6 pt-6 pb-6">
                                {availableAccountTypes.length > 1 && (
                                    <div className="mb-6">
                                        <label className="block mb-3 text-sm font-medium text-gray-700">
                                            Bank Account Types <span className="text-red-500">*</span>
                                        </label>
                                        <div className="space-y-2">
                                            {availableAccountTypes.map(accountType => (
                                                <label key={accountType} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedAccountTypes.includes(accountType)}
                                                        onChange={(e) => handleAccountTypeSelection(accountType, e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700 capitalize">
                                                        {accountType} Account
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="mt-2 text-sm text-gray-500">
                                            Select the bank account types that will be created for this sub-investor.
                                            Types are inherited from your custodian account.
                                        </p>
                                    </div>
                                )}

                                {/* Bank Account Forms */}
                                {selectedAccountTypes.map(accountType => (
                                    <div key={accountType} className="p-4 mb-6 border border-gray-200 rounded-lg">
                                        <h4 className="mb-4 font-medium text-gray-900 capitalize text-md">
                                            {accountType} Bank Account Details
                                        </h4>

                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Bank Name <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={bankAccountForms[accountType]?.bankName || ''}
                                                    onChange={(e) => handleBankAccountFormChange(accountType, 'bankName', e.target.value)}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                    required
                                                >
                                                    <option value="">Select bank</option>
                                                    <option value="Maybank">Maybank</option>
                                                    <option value="CIMB Bank">CIMB Bank</option>
                                                    <option value="Public Bank">Public Bank</option>
                                                    <option value="RHB Bank">RHB Bank</option>
                                                    <option value="Hong Leong Bank">Hong Leong Bank</option>
                                                    <option value="AmBank">AmBank</option>
                                                    <option value="Bank Islam">Bank Islam</option>
                                                    <option value="Bank Muamalat">Bank Muamalat</option>
                                                    <option value="Affin Bank">Affin Bank</option>
                                                    <option value="Alliance Bank">Alliance Bank</option>
                                                    <option value="Standard Chartered">Standard Chartered</option>
                                                    <option value="HSBC Bank">HSBC Bank</option>
                                                    <option value="OCBC Bank">OCBC Bank</option>
                                                    <option value="UOB Bank">UOB Bank</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Account Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={bankAccountForms[accountType]?.accountName || ''}
                                                    onChange={(e) => handleBankAccountFormChange(accountType, 'accountName', e.target.value)}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                    placeholder="Enter account holder name"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Account Number <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={bankAccountForms[accountType]?.accountNumber || ''}
                                                    onChange={(e) => handleBankAccountFormChange(accountType, 'accountNumber', e.target.value)}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                    placeholder="Enter account number"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Account Category <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={bankAccountForms[accountType]?.accountCategory || 'current'}
                                                    onChange={(e) => handleBankAccountFormChange(accountType, 'accountCategory', e.target.value)}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                    required
                                                >
                                                    <option value="current">Current</option>
                                                    <option value="savings">Savings</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    SWIFT/BIC <span className="text-sm text-gray-400">(Optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={bankAccountForms[accountType]?.swiftBic || ''}
                                                    onChange={(e) => handleBankAccountFormChange(accountType, 'swiftBic', e.target.value)}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                    placeholder="Enter SWIFT/BIC code"
                                                />
                                            </div>

                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Branch Code <span className="text-sm text-gray-400">(Optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={bankAccountForms[accountType]?.branchCode || ''}
                                                    onChange={(e) => handleBankAccountFormChange(accountType, 'branchCode', e.target.value)}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                    placeholder="Enter branch code"
                                                />
                                            </div>
                                        </div>

                                        {/* Shariah Compliance Document for Islamic accounts */}
                                        {accountType === 'islamic' && (
                                            <div className="mt-4">
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Shariah Compliance Declaration <span className="text-red-500">*</span>
                                                </label>
                                                <div className="p-4 text-center transition-colors border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        className="hidden"
                                                        id={`${accountType}_shariahCompliance`}
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                handleShariahDocumentUpload(accountType, file);
                                                            }
                                                        }}
                                                        required
                                                    />
                                                    <label htmlFor={`${accountType}_shariahCompliance`} className="cursor-pointer">
                                                        <svg className="w-6 h-6 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                        </svg>
                                                        <p className="text-sm text-gray-600">
                                                            {bankAccountForms[accountType]?.shariahComplianceDocument ?
                                                                bankAccountForms[accountType].shariahComplianceDocument.name :
                                                                'Upload Shariah Compliance Declaration'
                                                            }
                                                        </p>
                                                        <p className="mt-1 text-xs text-gray-500">
                                                            Supports PDF, JPG, PNG (max 10MB)
                                                        </p>
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="p-6 rounded-lg ">
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setActiveView('list')}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitNewInvestorApplication}
                                    disabled={
                                        createInvestorMutation.isPending ||
                                        !newInvestorForm.name ||
                                        !newInvestorForm.email ||
                                        !newInvestorForm.initialPassword ||
                                        newInvestorForm.initialPassword.length < 8 ||
                                        !newInvestorForm.registrationNumber ||
                                        !newInvestorForm.ssaName ||
                                        !newInvestorForm.ssaAccountNumber ||
                                        !newInvestorForm.custodyAccountNumber ||
                                        !newInvestorForm.residencyStatus ||
                                        !newInvestorForm.lei ||
                                        selectedAccountTypes.length === 0 ||
                                        selectedAccountTypes.some(accountType => {
                                            const form = bankAccountForms[accountType];
                                            return !form?.bankName || !form?.accountName || !form?.accountNumber;
                                        })
                                    }
                                    className="btn-primary"
                                >
                                    {createInvestorMutation.isPending ? (
                                        <>
                                            <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                            Creating...
                                        </>
                                    ) : (
                                        'Submit Application'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : activeView === 'upload-batch' ? (
                    /* Batch Upload View */
                    <div className="space-y-6">
                        {/* Header */}
                        <div className="p-6 rounded-lg">
                            <div className="flex items-center">
                                <button
                                    onClick={() => setActiveView('list')}
                                    className="p-2 mr-4 text-gray-400 transition-colors rounded-md hover:text-gray-600 hover:bg-gray-100"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Batch Upload</h2>
                                    <p className="mt-1 text-sm text-gray-500">Upload a CSV file containing multiple investor applications.</p>
                                </div>
                            </div>
                        </div>

                        {/* File Upload */}
                        <div className="p-6 bg-white border rounded-lg border-light-gray">
                            <div className="p-8 border-2 border-gray-300 border-dashed rounded-lg">
                                {/* Upload Area */}
                                <div className="text-center">
                                    <FileText className="w-12 h-12 mx-auto text-gray-400" />
                                    <div className="mt-4">
                                        <label htmlFor="batch-file" className="cursor-pointer">
                                            <span className="block mt-2 text-sm font-medium text-gray-900">
                                                {batchFile ? batchFile.name : 'Choose CSV file or drag and drop'}
                                            </span>
                                            <span className="block mt-1 text-xs text-gray-500">
                                                CSV files up to 10MB
                                            </span>
                                        </label>
                                        <input
                                            id="batch-file"
                                            type="file"
                                            className="hidden"
                                            accept=".csv"
                                            onChange={(e) => setBatchFile(e.target.files[0])}
                                        />
                                    </div>
                                </div>

                                {/* CSV Format Requirements */}
                                <div className="p-6 pt-6 mt-6 border border-gray-200">
                                    <h3 className="mb-4 text-lg font-medium text-gray-900">CSV Format Requirements</h3>
                                    <div className="p-4 rounded-lg bg-gray-50">
                                        <p className="mb-2 text-sm text-gray-700"><strong>Required columns:</strong></p>
                                        <code className="block mb-2 text-xs text-gray-600">
                                            Master Acct No., Master Acct Name, Custody Acct No., Custody Acct Name, Email, Cash Acc No CORPORATE ACTION.
                                        </code>
                                        <p className="mt-1 text-xs text-gray-500">
                                            <strong>Note:</strong> These fields are mandatory for all sub-investors.
                                        </p>

                                        <div className="p-3 mt-3 border border-blue-200 rounded bg-blue-50">
                                            <p className="mb-1 text-xs font-semibold text-gray-700">Format Details:</p>
                                            <p className="mt-1 text-xs text-gray-600">
                                                • <strong>Master Acct No./Name:</strong> Custodian's master account in Maybank system<br />
                                                • <strong>Custody Acct No./Name:</strong> Sub-investor's custody account details<br />
                                                • <strong>Email:</strong> Sub-investor's email address for account access<br />
                                                • <strong>Cash Acc No CORPORATE ACTION:</strong> Account for corporate action transactions<br />
                                                • <strong>File format:</strong> CSV - supports both comma-delimited and tab-delimited
                                            </p>
                                        </div>

                                        <p className="mt-4 mb-2 text-sm text-gray-700"><strong>Optional columns:</strong></p>
                                        <code className="block text-xs text-gray-600">
                                            Custody Acct Status, Country, Account open date, Domicile, CDS Acct No., CDS Acct Name.,
                                            Cash Acc Name CORPORATE ACTION, Cash Acc No LOCAL TRADE., Cash Acc Name LOCAL TRADE,
                                            Cash Acc No GLOBAL TRADE., Cash Acc Name GLOBAL TRADE, Cash Acc No BILLING.,
                                            Cash Acc Name BILLING, Maker, Maker Date, Checker, Checker Date, LEI No, Wallet No
                                        </code>
                                        <p className="mt-1 text-xs text-gray-500">
                                            <strong>Note:</strong> These fields are optional and can be left empty.
                                        </p>
                                    </div>
                                    <div className="mt-4">
                                        <button
                                            onClick={handleDownloadTemplate}
                                            className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            Download CSV Template
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {batchFile && (
                                <div className="p-4 mt-4 rounded-lg bg-blue-50">
                                    <div className="flex items-center">
                                        <FileText className="w-5 h-5 text-blue-600" />
                                        <span className="ml-2 text-sm text-blue-900">
                                            {batchFile.name} ({Math.round(batchFile.size / 1024)} KB)
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Upload Results */}
                        {batchUploadResults && (
                            <div className="p-6 bg-white rounded-lg shadow">
                                <h3 className="mb-4 text-lg font-medium text-gray-900">Upload Results</h3>
                                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3">
                                    <div className="p-4 rounded-lg bg-green-50">
                                        <div className="flex items-center">
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                            <span className="ml-2 text-sm font-medium text-green-900">Successful</span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-600">{batchUploadResults.successful}</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-red-50">
                                        <div className="flex items-center">
                                            <XCircle className="w-5 h-5 text-red-600" />
                                            <span className="ml-2 text-sm font-medium text-red-900">Failed</span>
                                        </div>
                                        <p className="text-2xl font-bold text-red-600">{batchUploadResults.failed}</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-blue-50">
                                        <div className="flex items-center">
                                            <FileText className="w-5 h-5 text-blue-600" />
                                            <span className="ml-2 text-sm font-medium text-blue-900">Total</span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-600">{batchUploadResults.total}</p>
                                    </div>
                                </div>

                                {batchUploadResults.errors.length > 0 && (
                                    <div className="mt-4">
                                        <h4 className="mb-2 text-sm font-medium text-gray-900">Errors:</h4>
                                        <div className="p-3 overflow-y-auto rounded-lg bg-red-50 max-h-40">
                                            {batchUploadResults.errors.map((error, index) => (
                                                <p key={index} className="mb-1 text-xs text-red-700">{error}</p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Submit Buttons */}
                        <div className="p-6 rounded-lg">
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setActiveView('list');
                                        setBatchFile(null);
                                        setBatchUploadResults(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    {batchUploadResults ? 'Back to List' : 'Cancel'}
                                </button>
                                {!batchUploadResults && (
                                    <button
                                        onClick={handleBatchUpload}
                                        disabled={!batchFile || isUploading}
                                        className="btn-primary"
                                    >
                                        {isUploading ? (
                                            <>
                                                <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            'Upload Batch'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Application Details View */
                    selectedApplication && (
                        <div className="space-y-6">
                            {/* Application Header */}
                            <div className="flex items-center mb-4">
                                <button
                                    onClick={() => setActiveView('list')}
                                    className="p-2 mr-4 text-gray-400 transition-colors rounded-md hover:text-gray-600 hover:bg-gray-100"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-6 bg-white border rounded-lg border-light-gray">

                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 w-12 h-12">
                                                <div className="flex items-center justify-center w-12 h-12 bg-gray-300 rounded-full">
                                                    {selectedApplication.applicationType === 'KYC' ? (
                                                        <User className="w-6 h-6 text-gray-600" />
                                                    ) : (
                                                        <Building2 className="w-6 h-6 text-gray-600" />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <h1 className="text-2xl font-bold text-gray-900">
                                                    {selectedApplication.applicantName}
                                                </h1>
                                                <p className="text-gray-600">
                                                    Application ID: {formatApplicationId(selectedApplication.id)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedApplication.status).class}`}>
                                            {getStatusIcon(selectedApplication.status)}
                                            <span className="ml-2">{getStatusBadge(selectedApplication.status).label}</span>
                                        </span>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Submitted: {formatDateTime(selectedApplication.submissionDate)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                                {/* Application Details */}
                                <div className="space-y-6 lg:col-span-2">
                                    {/* Applicant Information */}
                                    <div className="bg-white border rounded-lg border-light-gray">
                                        <div className="px-6 pt-6">
                                            <h3 className="text-lg font-medium text-gray-900">
                                                {selectedApplication.applicationType === 'KYC' ? 'Personal Information' : 'Company Information'}
                                            </h3>
                                        </div>
                                        <div className="my-4 border-b border-light-gray"></div>
                                        <div className="px-6 pb-6">
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                {selectedApplication.applicationType === 'KYC' ? (
                                                    <>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Full Name</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.applicantName}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">ID Number</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.idNumber}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Nationality</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.nationality}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Occupation</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.occupation}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Annual Income</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.annualIncome}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Source of Funds</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.sourceOfFunds}</p>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Company Name</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.applicantName}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Registration/Account Number</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.registrationNumber}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Incorporation Date</label>
                                                            <p className="mt-1 text-sm text-gray-900">{formatDate(selectedApplication.incorporationDate)}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Paid Up Capital</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.paidUpCapital}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">SSA Name</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.ssaName || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">SSA Account Number</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.ssaAccountNumber || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Custody Account Number</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.custodyAccountNumber || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Residency Status</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.residencyStatus || '-'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">LEI (Legal Entity Identifier)</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.lei || '-'}</p>
                                                        </div>
                                                    </>
                                                )}
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-500">Address</label>
                                                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.address}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500">Email</label>
                                                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.email}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-500">Phone</label>
                                                    <p className="mt-1 text-sm text-gray-900">{selectedApplication.phone}</p>
                                                </div>
                                                {selectedApplication.temporaryPassword && (
                                                    <div className="md:col-span-2">
                                                        <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50">
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <label className="flex items-center text-sm font-medium text-blue-900">
                                                                        <AlertCircle className="w-4 h-4 mr-1" />
                                                                        Temporary Password (Auto-generated)
                                                                    </label>
                                                                    <p className="mt-2 text-lg font-mono font-semibold text-blue-900">{selectedApplication.temporaryPassword}</p>
                                                                    <p className="mt-2 text-xs text-blue-700">⚠️ This password was auto-generated when the account was created. The user must change it on first login.</p>
                                                                </div>
                                                                <button
                                                                    onClick={() => copyToClipboard(selectedApplication.temporaryPassword)}
                                                                    className="flex items-center px-3 py-2 ml-4 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-100"
                                                                >
                                                                    <Copy className="w-4 h-4 mr-1" />
                                                                    Copy
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {selectedApplication.beneficialOwners && (
                                            <div className="mt-6">
                                                <h4 className="mb-3 font-medium text-gray-900 text-md">Beneficial Owners</h4>
                                                <div className="mb-3 border-b border-light-gray"></div>
                                                <div className="space-y-2">
                                                    {selectedApplication.beneficialOwners.map((owner, index) => (
                                                        <div key={index} className="flex items-center justify-between p-3 rounded-md bg-gray-50">
                                                            <div>
                                                                <p className="font-medium text-gray-900">{owner.name}</p>
                                                                <p className="text-sm text-gray-500">Ownership: {owner.percentage}</p>
                                                            </div>
                                                            <span className="text-sm text-gray-600">{owner.pepStatus}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Uploaded Documents */}
                                    <div className="bg-white border rounded-lg border-light-gray">
                                        <div className="px-6 pt-6">
                                            <h3 className="text-lg font-medium text-gray-900">Uploaded Documents</h3>
                                        </div>
                                        <div className="my-4 border-b border-light-gray"></div>
                                        <div className="px-6 pb-6">
                                            <div className="space-y-3">
                                                {selectedApplication.documents.map((document, index) => (
                                                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                                                        <div className="flex items-center">
                                                            <FileText className="w-5 h-5 mr-3 text-gray-400" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{document.name}</p>
                                                                <p className="text-xs text-gray-500">{document.type}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${document.status === 'uploaded' ? 'bg-blue-100 text-blue-800' :
                                                                document.status === 'verified' ? 'bg-green-100 text-green-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                {document.status}
                                                            </span>
                                                            {document.url && (
                                                                <button className="text-blue-600 hover:text-blue-800">
                                                                    <Download className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Review Notes */}
                                    <div className="bg-white border rounded-lg border-light-gray">
                                        <div className="px-6 pt-6">
                                            <h3 className="text-lg font-medium text-gray-900">Review Notes</h3>
                                        </div>
                                        <div className="my-4 border-b border-light-gray"></div>
                                        <div className="px-6 pb-6">
                                            <textarea
                                                value={reviewNotes}
                                                onChange={(e) => setReviewNotes(e.target.value)}
                                                rows={4}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Add your review notes, verification checklist, or internal comments..."
                                            />
                                        </div>
                                    </div>
                                </div>


                                <div className="lg:col-span-1">
                                    <div className="space-y-6">
                                        {/* Decision Actions */}
                                        {selectedApplication.status === 'pending_review' && (
                                            <div className="bg-white border rounded-lg border-light-gray">
                                                <div className="px-6 pt-6">
                                                    <h3 className="text-lg font-medium text-gray-900">Application Decision</h3>
                                                </div>
                                                <div className="my-4 border-b border-light-gray"></div>
                                                <div className="px-6 pb-6">
                                                    <div className="space-y-3">
                                                        <button
                                                            onClick={() => handleDecision('approve')}
                                                            className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                                        >
                                                            <CheckCircle className="w-4 h-4 mr-2" />
                                                            Approve Application
                                                        </button>
                                                        <button
                                                            onClick={() => handleDecision('more_info')}
                                                            className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        >
                                                            <MessageSquare className="w-4 h-4 mr-2" />
                                                            Request More Information
                                                        </button>
                                                        <button
                                                            onClick={() => handleDecision('reject')}
                                                            className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                        >
                                                            <XCircle className="w-4 h-4 mr-2" />
                                                            Reject Application
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Approval Information */}
                                        {selectedApplication.status === 'approved' && (
                                            <div className="bg-white border rounded-lg border-light-gray">
                                                <div className="px-6 pt-6">
                                                    <h3 className="text-lg font-medium text-gray-900">Approval Information</h3>
                                                </div>
                                                <div className="my-4 border-b border-light-gray"></div>
                                                <div className="px-6 pb-6">
                                                    <div className="space-y-2">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Approved Date</label>
                                                            <p className="mt-1 text-sm text-gray-900">{formatDateTime(selectedApplication.approvedDate)}</p>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-500">Reviewed By</label>
                                                            <p className="mt-1 text-sm text-gray-900">{selectedApplication.reviewedBy}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                )}
            </div>

            {/* Decision Confirmation Modals */}
            {showDecisionModal && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
                        <div className="mt-3">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full">
                                {showDecisionModal === 'approve' && <CheckCircle className="w-6 h-6 text-green-600" />}
                                {showDecisionModal === 'reject' && <XCircle className="w-6 h-6 text-red-600" />}
                                {showDecisionModal === 'more_info' && <MessageSquare className="w-6 h-6 text-blue-600" />}
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-medium text-gray-900">
                                    {showDecisionModal === 'approve' && 'Approve Application'}
                                    {showDecisionModal === 'reject' && 'Reject Application'}
                                    {showDecisionModal === 'more_info' && 'Request More Information'}
                                </h3>
                                <p className="mb-4 text-sm text-gray-500">
                                    {showDecisionModal === 'approve' && `Confirm approval for ${selectedApplication.applicantName}? This action will enable wallet creation for this investor.`}
                                    {showDecisionModal === 'reject' && `Please provide a reason for rejecting ${selectedApplication.applicantName}'s application.`}
                                    {showDecisionModal === 'more_info' && `Specify what additional information is needed from ${selectedApplication.applicantName}.`}
                                </p>

                                {showDecisionModal === 'reject' && (
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        rows={3}
                                        className="w-full mb-4 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        placeholder="Enter detailed reason for rejection..."
                                    />
                                )}

                                {showDecisionModal === 'more_info' && (
                                    <textarea
                                        value={requestMoreInfo}
                                        onChange={(e) => setRequestMoreInfo(e.target.value)}
                                        rows={3}
                                        className="w-full mb-4 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        placeholder="Specify what additional information or documents are needed..."
                                    />
                                )}
                            </div>
                            <div className="flex justify-end mt-6 space-x-3">
                                <button
                                    onClick={() => setShowDecisionModal(null)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDecision}
                                    disabled={
                                        (showDecisionModal === 'reject' && !rejectionReason.trim()) ||
                                        (showDecisionModal === 'more_info' && !requestMoreInfo.trim())
                                    }
                                    className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${showDecisionModal === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' :
                                        showDecisionModal === 'reject' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' :
                                            'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                                        }`}
                                >
                                    {showDecisionModal === 'approve' && 'Confirm Approval'}
                                    {showDecisionModal === 'reject' && 'Confirm Rejection'}
                                    {showDecisionModal === 'more_info' && 'Send Request'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Activate/Deactivate Confirmation Modal */}
            {showActivateModal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
                        <div className="mt-3">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full">
                                {showActivateModal === 'activate' && <CheckCircle className="w-6 h-6 text-green-600" />}
                                {showActivateModal === 'deactivate' && <XCircle className="w-6 h-6 text-red-600" />}
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-medium text-gray-900">
                                    {showActivateModal === 'activate' && 'Activate Sub-Investor'}
                                    {showActivateModal === 'deactivate' && 'Close Sub-Investor'}
                                </h3>
                                <p className="mb-4 text-sm text-gray-500">
                                    {showActivateModal === 'activate' &&
                                        `Are you sure you want to activate ${selectedSubInvestor.applicantName}? This will allow them to access the platform and perform transactions.`
                                    }
                                    {showActivateModal === 'deactivate' &&
                                        `Are you sure you want to close ${selectedSubInvestor.applicantName}? This will prevent them from accessing the platform and performing transactions.`
                                    }
                                </p>
                            </div>
                            <div className="flex justify-end mt-6 space-x-3">
                                <button
                                    onClick={() => {
                                        setShowActivateModal(false);
                                        setSelectedSubInvestor(null);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmActivateDeactivate}
                                    disabled={activateSubInvestorMutation.isPending || deactivateSubInvestorMutation.isPending}
                                    className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${showActivateModal === 'activate' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' :
                                        'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                        }`}
                                >
                                    {activateSubInvestorMutation.isPending || deactivateSubInvestorMutation.isPending ? (
                                        <>
                                            <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                            {showActivateModal === 'activate' ? 'Activating...' : 'Closing...'}
                                        </>
                                    ) : (
                                        showActivateModal === 'activate' ? 'Confirm Activation' : 'Confirm Close'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Sub-Investor Modal */}
            {showEditModal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-4xl p-5 mx-auto bg-white border rounded-md shadow-lg top-10">
                        <div className="mt-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Edit Sub-Investor: {selectedSubInvestor.applicantName}
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedSubInvestor(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="my-4 border-b border-light-gray"></div>

                            <div className="space-y-6">
                                {/* Required Fields */}
                                <div>
                                    <h4 className="mb-3 font-medium text-gray-800 text-md">Required Fields</h4>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Company Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.name}
                                                onChange={(e) => handleEditFormChange('name', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter company name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                value={editForm.email}
                                                onChange={(e) => handleEditFormChange('email', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter email address"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Registration Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.registrationNumber}
                                                onChange={(e) => handleEditFormChange('registrationNumber', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter registration number"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                value={editForm.phone}
                                                onChange={(e) => handleEditFormChange('phone', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter phone number"
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                SSA Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.ssaName}
                                                onChange={(e) => handleEditFormChange('ssaName', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter SSA name"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                SSA Account Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.ssaAccountNumber}
                                                onChange={(e) => handleEditFormChange('ssaAccountNumber', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter SSA account number"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Custody Account Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.custodyAccountNumber}
                                                onChange={(e) => handleEditFormChange('custodyAccountNumber', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter custody account number"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Residency Status <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                value={editForm.residencyStatus}
                                                onChange={(e) => handleEditFormChange('residencyStatus', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                required
                                            >
                                                <option value="">Select residency status</option>
                                                <option value="resident">Resident</option>
                                                <option value="non-resident">Non-Resident</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                LEI (Legal Entity Identifier) <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={editForm.lei}
                                                onChange={(e) => handleEditFormChange('lei', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter LEI"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Optional Fields */}
                                <div>
                                    <h4 className="mb-3 font-medium text-gray-800 text-md">Optional Fields</h4>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Entity Type
                                            </label>
                                            <select
                                                value={editForm.entityType}
                                                onChange={(e) => handleEditFormChange('entityType', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            >
                                                <option value="">Select entity type</option>
                                                <option value="Private Limited">Private Limited Company</option>
                                                <option value="Public Limited">Public Limited Company</option>
                                                <option value="Partnership">Partnership</option>
                                                <option value="Trust">Trust</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Incorporation Date
                                            </label>
                                            <input
                                                type="date"
                                                value={editForm.incorporationDate}
                                                onChange={(e) => handleEditFormChange('incorporationDate', e.target.value)}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Address
                                            </label>
                                            <textarea
                                                value={editForm.address}
                                                onChange={(e) => handleEditFormChange('address', e.target.value)}
                                                rows={3}
                                                className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                placeholder="Enter full address"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end mt-6 space-x-3">
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedSubInvestor(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitEditForm}
                                    disabled={updateSubInvestorMutation.isPending}
                                    className="btn-primary"
                                >
                                    {updateSubInvestorMutation.isPending ? (
                                        <>
                                            <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Sub-Investor'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {showResetPasswordModal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
                        <div className="mt-3">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full">
                                <Key className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-medium text-gray-900">
                                    Reset Password for {selectedSubInvestor.applicantName}
                                </h3>
                                {!resetPasswordResult ? (
                                    <>
                                        <p className="mb-4 text-sm text-gray-500">
                                            This will generate a new random password for {selectedSubInvestor.applicantName}. The new password will be displayed after the reset.
                                        </p>
                                        <div className="flex justify-end mt-6 space-x-3">
                                            <button
                                                onClick={() => {
                                                    setShowResetPasswordModal(false);
                                                    setSelectedSubInvestor(null);
                                                    setResetPasswordResult(null);
                                                }}
                                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={confirmResetPassword}
                                                disabled={resetPasswordMutation.isPending}
                                                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {resetPasswordMutation.isPending ? (
                                                    <>
                                                        <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                                        Resetting...
                                                    </>
                                                ) : (
                                                    'Reset Password'
                                                )}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="p-4 mb-4 border border-green-200 rounded-lg bg-green-50">
                                            <p className="mb-2 text-sm text-green-800">
                                                <strong>Password reset successful!</strong>
                                            </p>
                                            <p className="mb-2 text-sm text-green-700">
                                                New password for {selectedSubInvestor.applicantName}:
                                            </p>
                                            <div className="p-2 bg-white border border-green-300 rounded">
                                                <code className="font-mono text-sm text-green-800 break-all">
                                                    {resetPasswordResult}
                                                </code>
                                            </div>
                                            <p className="mt-2 text-xs text-green-600">
                                                Please provide this password to the sub-investor securely.
                                            </p>
                                        </div>
                                        <div className="flex justify-end mt-6 space-x-3">
                                            <button
                                                onClick={() => {
                                                    setShowResetPasswordModal(false);
                                                    setSelectedSubInvestor(null);
                                                    setResetPasswordResult(null);
                                                }}
                                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Sub-Investor Modal */}
            {showDeleteModal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
                        <div className="mt-3">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-medium text-gray-900">
                                    Delete Sub-Investor: {selectedSubInvestor.applicantName}
                                </h3>
                                <p className="mb-4 text-sm text-gray-500">
                                    This action cannot be undone. This will permanently delete the sub-investor account and all associated data.
                                </p>
                                <p className="mb-4 text-sm text-red-600">
                                    <strong>Warning:</strong> Sub-investors with existing wallets cannot be deleted. Please deactivate the account instead.
                                </p>
                            </div>
                            <div className="flex justify-end mt-6 space-x-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedSubInvestor(null);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteSubInvestor}
                                    disabled={deleteSubInvestorMutation.isPending}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {deleteSubInvestorMutation.isPending ? (
                                        <>
                                            <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete Sub-Investor'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Approve L2 Modal */}
            {showApproveL2Modal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
                        <div className="mt-3">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                                <CheckSquare className="w-6 h-6 text-green-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-medium text-gray-900">
                                    Approve L2: {selectedSubInvestor.applicantName}
                                </h3>
                                <p className="mb-4 text-sm text-gray-500">
                                    This will approve the sub-investor account and make it active. The account will be able to log in and perform transactions.
                                </p>
                                <div className="mb-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-700">
                                        Comments (Optional)
                                    </label>
                                    <textarea
                                        value={approvalComments}
                                        onChange={(e) => setApprovalComments(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        rows={3}
                                        placeholder="Add any comments about this approval..."
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-6 space-x-3">
                                <button
                                    onClick={() => {
                                        setShowApproveL2Modal(false);
                                        setSelectedSubInvestor(null);
                                        setApprovalComments('');
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmApproveL2}
                                    disabled={approveL2Mutation.isPending}
                                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {approveL2Mutation.isPending ? (
                                        <>
                                            <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                            Approving...
                                        </>
                                    ) : (
                                        'Approve L2'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Sub-Investor Modal */}
            {showRejectModal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative w-11/12 max-w-md p-5 mx-auto bg-white border rounded-md shadow-lg top-20">
                        <div className="mt-3">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                                <XSquare className="w-6 h-6 text-red-600" />
                            </div>
                            <div className="text-center">
                                <h3 className="mb-2 text-lg font-medium text-gray-900">
                                    Reject Sub-Investor: {selectedSubInvestor.applicantName}
                                </h3>
                                <p className="mb-4 text-sm text-gray-500">
                                    This will reject the sub-investor account. The account will remain inactive and cannot be used.
                                </p>
                                <div className="mb-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-700">
                                        Rejection Reason <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={approvalRejectionReason}
                                        onChange={(e) => setApprovalRejectionReason(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        rows={3}
                                        placeholder="Please provide a reason for rejection..."
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block mb-2 text-sm font-medium text-gray-700">
                                        Additional Comments (Optional)
                                    </label>
                                    <textarea
                                        value={approvalRejectionComments}
                                        onChange={(e) => setApprovalRejectionComments(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        rows={2}
                                        placeholder="Add any additional comments..."
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end mt-6 space-x-3">
                                <button
                                    onClick={() => {
                                        setShowRejectModal(false);
                                        setSelectedSubInvestor(null);
                                        setApprovalRejectionReason('');
                                        setApprovalRejectionComments('');
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmRejectSubInvestor}
                                    disabled={rejectSubInvestorMutation.isPending || !approvalRejectionReason.trim()}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {rejectSubInvestorMutation.isPending ? (
                                        <>
                                            <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full mr-2" />
                                            Rejecting...
                                        </>
                                    ) : (
                                        'Reject Sub-Investor'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Approve Request Confirmation Modal */}
            {showApproveRequestModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
                    <div className="w-11/12 max-w-md p-6 mx-auto bg-white border rounded-md shadow-lg">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                            <CheckSquare className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="text-center">
                            <h3 className="mb-2 text-lg font-medium text-gray-900">
                                Approve Request
                            </h3>
                            <p className="mb-6 text-sm text-gray-500">
                                {selectedPendingRequest?.actionType === 'create_sub_investor'
                                    ? `Approve the creation of sub-investor: ${selectedPendingRequest?.targetData?.name}`
                                    : `Approve ${selectedPendingRequest?.actionType} request for: ${selectedPendingRequest?.targetData?.name}`
                                }
                            </p>
                            <div className="flex justify-center space-x-3">
                                <button
                                    onClick={() => {
                                        setShowApproveRequestModal(false);
                                        setSelectedPendingRequest(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmApproveRequest}
                                    disabled={approveRequestMutation.isLoading}
                                    className="btn-primary"
                                >
                                    {approveRequestMutation.isLoading ? 'Approving...' : 'Approve Request'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Request Confirmation Modal */}
            {showRejectRequestModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
                    <div className="w-11/12 max-w-md p-6 mx-auto bg-white border rounded-md shadow-lg">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                            <XSquare className="w-6 h-6 text-red-600" />
                        </div>
                        <div className="text-center">
                            <h3 className="mb-2 text-lg font-medium text-gray-900">
                                Reject Request
                            </h3>
                            <p className="mb-6 text-sm text-gray-500">
                                {selectedPendingRequest?.actionType === 'create_sub_investor'
                                    ? `Reject the creation of sub-investor: ${selectedPendingRequest?.targetData?.name}`
                                    : `Reject ${selectedPendingRequest?.actionType} request for: ${selectedPendingRequest?.targetData?.name}`
                                }
                            </p>
                            <div className="flex justify-center space-x-3">
                                <button
                                    onClick={() => {
                                        setShowRejectRequestModal(false);
                                        setSelectedPendingRequest(null);
                                    }}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmRejectRequest}
                                    disabled={rejectRequestMutation.isLoading}
                                    className="btn-primary"
                                >
                                    {rejectRequestMutation.isLoading ? 'Rejecting...' : 'Reject Request'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Portal-based Actions Dropdown */}
            {showActionsDropdown && createPortal(
                <div
                    className="fixed w-48 bg-white border border-gray-200 rounded-md shadow-xl actions-dropdown ring-1 ring-black ring-opacity-5"
                    style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        zIndex: 9999
                    }}
                >
                    <div className="py-1">
                        {(() => {
                            const currentApplication = applications.find(app => app.id === showActionsDropdown);
                            if (!currentApplication) return null;

                            return (
                                <>
                                    <button
                                        onClick={() => handleViewApplication(currentApplication)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <Eye className="w-4 h-4 mr-3" />
                                        Review
                                    </button>

                                    <button
                                        onClick={() => handleEditSubInvestor(currentApplication)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <FileText className="w-4 h-4 mr-3" />
                                        Edit
                                    </button>

                                    <button
                                        onClick={() => handleManageWallets(currentApplication)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        <Wallet className="w-4 h-4 mr-3" />
                                        Manage Wallets
                                    </button>

                                    <button
                                        onClick={() => handleResetPassword(currentApplication)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                                    >
                                        <Key className="w-4 h-4 mr-3" />
                                        Reset Password
                                    </button>

                                    {currentApplication.approvalStatus === 'pending_l2' && (
                                        <button
                                            onClick={() => handleApproveL2(currentApplication)}
                                            className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                        >
                                            <CheckCircle className="w-4 h-4 mr-3" />
                                            Approve L2
                                        </button>
                                    )}

                                    {(currentApplication.approvalStatus === 'pending_l1' || currentApplication.approvalStatus === 'pending_l2') && (
                                        <button
                                            onClick={() => handleRejectSubInvestor(currentApplication)}
                                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                        >
                                            <XCircle className="w-4 h-4 mr-3" />
                                            Reject
                                        </button>
                                    )}

                                    {currentApplication.accountStatus === 'closed' && (
                                        <button
                                            onClick={() => handleActivateDeactivate(currentApplication, 'activate')}
                                            className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                        >
                                            <CheckCircle className="w-4 h-4 mr-3" />
                                            Activate
                                        </button>
                                    )}

                                    {currentApplication.accountStatus === 'active' && (
                                        <button
                                            onClick={() => handleActivateDeactivate(currentApplication, 'deactivate')}
                                            className="flex items-center w-full px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50"
                                        >
                                            <AlertTriangle className="w-4 h-4 mr-3" />
                                            Close
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleDeleteSubInvestor(currentApplication)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                    >
                                        <Trash2 className="w-4 h-4 mr-3" />
                                        Delete
                                    </button>
                                </>
                            );
                        })()}
                    </div>
                </div>,
                document.body
            )}

            {/* Wallet Management Modal */}
            {showWalletModal && selectedSubInvestor && (
                <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
                    <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b">
                            <div>
                                <h2 className="text-xl font-semibold">Wallet Management</h2>
                                <p className="text-gray-600">{selectedSubInvestor.applicantName}</p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowWalletModal(false);
                                    setEditingWallet(null);
                                    setEditBankAccountForm({});
                                }}
                                style={{ color: '#9ca3af' }}
                                className="hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6">
                            {subInvestorWallets.length === 0 ? (
                                <div className="py-8 text-center">
                                    <Wallet className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                    <h3 className="mb-2 text-lg font-medium text-gray-900">No Wallets Found</h3>
                                    <p className="text-gray-600">This sub-investor doesn't have any wallets yet.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Wallet Overview */}
                                    <div className="mb-6">
                                        <h3 className="mb-4 text-lg font-medium">Wallet Overview</h3>
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            {subInvestorWallets.map(wallet => (
                                                <div key={wallet._id} className="p-4 transition-shadow border rounded-lg hover:shadow-md">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center space-x-2">
                                                            <div className={`w-3 h-3 rounded-full ${wallet.bankAccountDetails.accountType === 'conventional' ? 'bg-blue-500' : 'bg-green-500'
                                                                }`} />
                                                            <h4 className="font-medium capitalize">{wallet.bankAccountDetails.accountType} Wallet</h4>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded-full text-xs ${wallet.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                            }`}>
                                                            {wallet.status}
                                                        </span>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <div>
                                                            <label className="text-sm text-gray-500">Wallet Address</label>
                                                            <div className="flex items-center space-x-2">
                                                                <code className="flex-1 px-2 py-1 text-sm bg-gray-100 rounded">
                                                                    {wallet.walletAddress.slice(0, 10)}...{wallet.walletAddress.slice(-8)}
                                                                </code>
                                                                <button
                                                                    onClick={() => copyToClipboard(wallet.walletAddress)}
                                                                    className="text-gray-400 hover:text-gray-600"
                                                                    title="Copy full address"
                                                                >
                                                                    <Copy className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="text-sm text-gray-500">Network</label>
                                                            <p className="text-sm">{wallet.network}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Bank Account Details */}
                                    <div className="mb-6">
                                        <h3 className="mb-4 text-lg font-medium">Bank Account Information</h3>
                                        <div className="space-y-4">
                                            {subInvestorWallets.map(wallet => (
                                                <div key={wallet._id} className="p-4 border rounded-lg">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="font-medium capitalize">{wallet.bankAccountDetails.accountType} Account</h4>
                                                        <button
                                                            onClick={() => handleEditBankAccount(wallet)}
                                                            className="text-sm text-blue-600 hover:text-blue-800"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <label className="text-gray-500">Bank Name</label>
                                                            <p>{wallet.bankAccountDetails.bankName || 'Not specified'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-gray-500">Account Name</label>
                                                            <p>{wallet.bankAccountDetails.accountName || 'Not specified'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-gray-500">Account Number</label>
                                                            <p>{wallet.bankAccountDetails.accountNumber || 'Not specified'}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-gray-500">Account Category</label>
                                                            <p className="capitalize">{wallet.bankAccountDetails.accountCategory || 'Not specified'}</p>
                                                        </div>
                                                        {wallet.bankAccountDetails.swiftBic && (
                                                            <div>
                                                                <label className="text-gray-500">SWIFT/BIC</label>
                                                                <p>{wallet.bankAccountDetails.swiftBic}</p>
                                                            </div>
                                                        )}
                                                        {wallet.bankAccountDetails.branchCode && (
                                                            <div>
                                                                <label className="text-gray-500">Branch Code</label>
                                                                <p>{wallet.bankAccountDetails.branchCode}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Edit Bank Account Form */}
                                    {editingWallet && (
                                        <div className="pt-6 border-t">
                                            <h3 className="mb-4 text-lg font-medium">Edit Bank Account Details</h3>
                                            <form onSubmit={(e) => { e.preventDefault(); handleUpdateBankAccount(); }}>
                                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                    <div>
                                                        <label className="block mb-1 text-sm font-medium text-gray-700">Bank Name *</label>
                                                        <select
                                                            value={editBankAccountForm.bankName}
                                                            onChange={(e) => handleEditBankAccountFormChange('bankName', e.target.value)}
                                                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                            required
                                                        >
                                                            <option value="">Select bank</option>
                                                            <option value="Maybank">Maybank</option>
                                                            <option value="CIMB Bank">CIMB Bank</option>
                                                            <option value="Public Bank">Public Bank</option>
                                                            <option value="RHB Bank">RHB Bank</option>
                                                            <option value="Hong Leong Bank">Hong Leong Bank</option>
                                                            <option value="AmBank">AmBank</option>
                                                            <option value="Bank Islam">Bank Islam</option>
                                                            <option value="Bank Muamalat">Bank Muamalat</option>
                                                            <option value="Affin Bank">Affin Bank</option>
                                                            <option value="Alliance Bank">Alliance Bank</option>
                                                            <option value="Standard Chartered">Standard Chartered</option>
                                                            <option value="HSBC Bank">HSBC Bank</option>
                                                            <option value="OCBC Bank">OCBC Bank</option>
                                                            <option value="UOB Bank">UOB Bank</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block mb-1 text-sm font-medium text-gray-700">Account Name *</label>
                                                        <input
                                                            type="text"
                                                            value={editBankAccountForm.accountName}
                                                            onChange={(e) => handleEditBankAccountFormChange('accountName', e.target.value)}
                                                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block mb-1 text-sm font-medium text-gray-700">Account Number *</label>
                                                        <input
                                                            type="text"
                                                            value={editBankAccountForm.accountNumber}
                                                            onChange={(e) => handleEditBankAccountFormChange('accountNumber', e.target.value)}
                                                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                            required
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block mb-1 text-sm font-medium text-gray-700">Account Category *</label>
                                                        <select
                                                            value={editBankAccountForm.accountCategory}
                                                            onChange={(e) => handleEditBankAccountFormChange('accountCategory', e.target.value)}
                                                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                            required
                                                        >
                                                            <option value="">Select account category</option>
                                                            <option value="current">Current</option>
                                                            <option value="savings">Savings</option>
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block mb-1 text-sm font-medium text-gray-700">SWIFT/BIC</label>
                                                        <input
                                                            type="text"
                                                            value={editBankAccountForm.swiftBic || ''}
                                                            onChange={(e) => handleEditBankAccountFormChange('swiftBic', e.target.value)}
                                                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                            placeholder="Enter SWIFT/BIC code"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block mb-1 text-sm font-medium text-gray-700">Branch Code</label>
                                                        <input
                                                            type="text"
                                                            value={editBankAccountForm.branchCode || ''}
                                                            onChange={(e) => handleEditBankAccountFormChange('branchCode', e.target.value)}
                                                            className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                            placeholder="Enter branch code"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex justify-end mt-4 space-x-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setEditingWallet(null);
                                                            setEditBankAccountForm({});
                                                        }}
                                                        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                                                    >
                                                        Update Bank Details
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="flex justify-end px-6 py-3 border-t">
                            <button
                                onClick={() => {
                                    setShowWalletModal(false);
                                    setEditingWallet(null);
                                    setEditBankAccountForm({});
                                }}
                                className="btn-secondary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubInvestor; 